import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    Stepper,
    Step,
    StepLabel,
    StepContent,
    Button,
    Alert,
    LinearProgress,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Divider,
    TextField,
    Grid2,
    IconButton,
    Tooltip
} from '@mui/material';
import {
    PlayArrow as StartIcon,
    CheckCircle as CompleteIcon,
    Error as ErrorIcon,
    Warning as WarningIcon,
    Schedule as TimeIcon,
    Security as SecurityIcon,
    ArrowForward as NextIcon,
    ArrowBack as BackIcon,
    Refresh as RetryIcon,
    ContentCopy as CopyIcon,
    Launch as LaunchIcon,
    Close as CloseIcon
} from '@mui/icons-material';
import {
    WorkflowTemplate,
    WorkflowStep,
    validateWorkflowStep,
    resolveWorkflowInputs,
    calculateWorkflowProgress,
    estimateRemainingTime
} from '../../utils/workflowTemplates';
import { useWallet } from '../context/WagmiWalletContext';
import { useContracts } from '../context/ContractContext';
import TransactionBuilder from '../transaction-builder/TransactionBuilder';
import FunctionExplorer from '../transaction-builder/FunctionExplorer';
import TimelockManager from '../timelock/TimelockManager';
import SaltGenerator from '../transaction-builder/SaltGenerator';
import DelayHelper from '../transaction-builder/DelayHelper';
import { FunctionBuilderStep } from './steps';
import secureWorkflowDataService from '../../services/secureWorkflowDataService';
import backendDataHandler from '../../services/backendDataHandler';
import SecureWorkflowDataDisplay from '../common/SecureWorkflowDataDisplay';
import { useSignatureContext } from '../context/SignatureContext';
import SignatureProgressDisplay from '../transaction-builder/SignatureProgressDisplay';
import AddressSelector from '../common/AddressSelector';
import workflowCompletionService, { WorkflowCompletionData, ScheduleData } from '../../services/workflowCompletionService';
import logger from '../../utils/logger';
import { ethers } from 'ethers';
import { extractSaltFromScheduleFunction, computeOperationId } from '../../utils/timelockDecoder';
import saltStorageService from '../../services/saltStorageService';

interface WorkflowExecutorProps {
    template: WorkflowTemplate;
    onComplete: (results: { [stepId: string]: any }) => void;
    onCancel: () => void;
    workflowContext: {
        activeWorkflows: { type: string; key: string; timestamp: number }[];
        registerWorkflow: (type: string) => string;
        completeWorkflow: (key: string) => void;
    };
}

interface StepState {
    status: 'pending' | 'active' | 'completed' | 'error';
    outputs?: { [key: string]: any };
    error?: string;
    startTime?: number;
    endTime?: number;
}

const WorkflowExecutor: React.FC<WorkflowExecutorProps> = ({
    template,
    onComplete,
    onCancel,
    workflowContext
}) => {
    const { signer, isConnected } = useWallet();
    const { contracts } = useContracts();

    // Workflow state
    const [activeStep, setActiveStep] = useState(0);
    const [stepStates, setStepStates] = useState<{ [stepId: string]: StepState }>(() => {
        const initialStates: { [stepId: string]: StepState } = {};
        template.steps.forEach((step, index) => {
            initialStates[step.id] = {
                status: index === 0 ? 'active' : 'pending'
            };
        });
        return initialStates;
    });
    const [userInputs, setUserInputs] = useState<{ [stepId: string]: { [key: string]: string } }>({});
    const [workflowKey, setWorkflowKey] = useState<string>('');
    const [showStepDialog, setShowStepDialog] = useState(false);
    const [currentStepComponent, setCurrentStepComponent] = useState<React.ReactNode>(null);

    // Success Modal State - Normal workflow completion behavior
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [workflowResults, setWorkflowResults] = useState<{ [stepId: string]: any } | null>(null);

    // Initialize workflow
    useEffect(() => {
        const key = workflowContext.registerWorkflow(template.id);
        setWorkflowKey(key);
        logger.log('Started workflow:', template.name, 'with key:', key);

        return () => {
            if (key) {
                workflowContext.completeWorkflow(key);
            }
        };
    }, [template.id]); // Remove workflowContext from dependencies to prevent infinite loop

    // Listen for startExecuteWorkflow events from DatabaseMaintenancePanel and TimelockOperations
    useEffect(() => {
        const handleStartExecuteWorkflow = (event: CustomEvent) => {
            const { scheduleData, workflowName, transactionHash, completionId, operationId, source } = event.detail;

            logger.log('🎯 WorkflowExecutor: Received startExecuteWorkflow event:', {
                scheduleData,
                workflowName,
                transactionHash,
                completionId,
                operationId,
                source
            });

            // Only handle if this is a timelock_to_multisig workflow
            if (template.id === 'timelock_to_multisig' && scheduleData) {
                // Pre-populate the schedule selection step with the provided data
                const step0Data = {
                    target: scheduleData.target,
                    value: scheduleData.value,
                    data: scheduleData.data,
                    predecessor: scheduleData.predecessor,
                    salt: scheduleData.salt,
                    delay: scheduleData.delay,
                    workflowName: workflowName,
                    transactionHash: transactionHash,
                    completionId: completionId || operationId,
                    source: source,
                    description: `Auto-selected schedule from ${source || 'external source'}`
                };

                logger.log('✅ Auto-completing Step 0 with provided schedule data:', step0Data);

                // Auto-complete Step 0 (schedule selection) with the provided data
                updateStepState('step0_schedule_selection', {
                    status: 'completed',
                    outputs: step0Data,
                    endTime: Date.now()
                });

                // Move to Step 1 (timelock execute builder)
                updateStepState('step1_timelock_execute_builder', {
                    status: 'active',
                    startTime: Date.now()
                });

                // Update active step to Step 1
                setActiveStep(1);

                // Store step outputs for next step to access
                try {
                    sessionStorage.setItem('step_step0_schedule_selection_outputs', JSON.stringify(step0Data));
                    sessionStorage.setItem('previousStepOutputs', JSON.stringify(step0Data));
                    logger.log('✅ Stored auto-selected schedule data in sessionStorage');
                } catch (error) {
                    logger.error('❌ Error storing auto-selected schedule data:', error);
                }
            }
        };

        const eventListener = (event: Event) => {
            handleStartExecuteWorkflow(event as CustomEvent);
        };

        window.addEventListener('startExecuteWorkflow', eventListener);

        return () => {
            window.removeEventListener('startExecuteWorkflow', eventListener);
        };
    }, [template.id]);

    // Listen for function built events and handle workflow-specific storage
    useEffect(() => {
        const handleFunctionBuilt = async (event: CustomEvent) => {
            try {
                const { functionReference, secureReference, targetContract, functionName, inputCount, timestamp } = event.detail;

                // Determine current step number
                const currentStepIndex = template.steps.findIndex(step => stepStates[step.id].status === 'active');
                const currentStepNumber = currentStepIndex + 1;

                logger.log('🎯 Workflow received functionBuilt event:', {
                    functionReference,
                    currentStepNumber,
                    functionName,
                    targetContract
                });

                // Create workflow-specific storage with proper step naming
                const workflowStepId = `wf_step${currentStepNumber}_${timestamp}_${Math.random().toString(36).substr(2, 9)}`;

                // SECURITY FIX: Don't decrypt and re-store data - just pass the secure reference
                logger.log('🔐 Security Fix: Passing secure reference without decryption');

                // Store workflow metadata with the SECURE REFERENCE (not decrypted data)
                const workflowMetadata = {
                    originalReference: functionReference,
                    secureReference: secureReference, // Keep the encrypted reference
                    targetContract,
                    functionName,
                    inputCount,
                    stepNumber: currentStepNumber,
                    stepId: template.steps[currentStepIndex]?.id,
                    workflowId: workflowKey,
                    workflowName: template.name,
                    timestamp,
                    isSecureReference: true // Flag to indicate this contains secure references
                };

                // Store the workflow reference in sessionStorage for the current step
                sessionStorage.setItem(`workflow_step${currentStepNumber}_reference`, secureReference);

                // Emit workflow-specific event for other components
                const workflowEvent = new CustomEvent('workflowFunctionStored', {
                    detail: {
                        stepNumber: currentStepNumber,
                        stepId: template.steps[currentStepIndex]?.id,
                        workflowReference: secureReference,
                        originalReference: secureReference,
                        functionName,
                        targetContract,
                        isSecureReference: true
                    }
                });
                window.dispatchEvent(workflowEvent);

                logger.log('✅ Workflow metadata stored without decrypting sensitive data');

            } catch (error) {
                logger.error('❌ Error handling functionBuilt event in workflow:', error);
            }
        };

        // Add event listener with proper typing
        const eventListener = (event: Event) => {
            handleFunctionBuilt(event as CustomEvent);
        };

        window.addEventListener('functionBuilt', eventListener);

        // Cleanup
        return () => {
            window.removeEventListener('functionBuilt', eventListener);
        };
    }, [template.steps, stepStates, workflowKey, template.name]);

    // Listen for salt generation events to store for workflow completion
    useEffect(() => {
        const handleSaltGenerated = (event: CustomEvent) => {
            const { salt, id, functionName } = event.detail;

            // Store salt in sessionStorage for workflow completion capture
            if (salt && functionName === 'schedule') {
                sessionStorage.setItem('lastGeneratedSalt', salt);
                logger.log('🧂 Stored generated salt for workflow completion:', salt);
            }
        };

        const eventListener = (event: Event) => {
            handleSaltGenerated(event as CustomEvent);
        };

        window.addEventListener('saltGenerated', eventListener);

        return () => {
            window.removeEventListener('saltGenerated', eventListener);
        };
    }, []);

    // Get current step
    const currentStep = template.steps[activeStep];
    const currentStepState = stepStates[currentStep?.id];

    // Calculate progress
    const completedSteps = Object.values(stepStates).filter(state => state.status === 'completed').length;
    const progress = calculateWorkflowProgress(
        Object.keys(stepStates).filter(stepId => stepStates[stepId].status === 'completed'),
        template.steps.length
    );
    const remainingTime = estimateRemainingTime(template,
        Object.keys(stepStates).filter(stepId => stepStates[stepId].status === 'completed')
    );

    // Update step state
    const updateStepState = (stepId: string, updates: Partial<StepState>) => {
        setStepStates(prev => ({
            ...prev,
            [stepId]: { ...prev[stepId], ...updates }
        }));
    };

    // Handle step completion
    const completeStep = (stepId: string, outputs: { [key: string]: any }) => {
        updateStepState(stepId, {
            status: 'completed',
            outputs,
            endTime: Date.now()
        });

        // Store step outputs in sessionStorage for next step to access
        try {
            sessionStorage.setItem(`step_${stepId}_outputs`, JSON.stringify(outputs));
            logger.log(`Stored step outputs in sessionStorage for ${stepId}:`, outputs);

            // Also store as previousStepOutputs for the next step
            sessionStorage.setItem('previousStepOutputs', JSON.stringify(outputs));

            // Store workflow context data
            const workflowContext = {
                currentStepId: stepId,
                isWorkflowContext: true,
                templateId: template.id,
                templateName: template.name
            };
            sessionStorage.setItem('workflowStepData', JSON.stringify(workflowContext));
        } catch (error) {
            logger.error('Error storing step outputs in sessionStorage:', error);
        }

        // Move to next step if available
        const currentIndex = template.steps.findIndex(step => step.id === stepId);
        if (currentIndex < template.steps.length - 1) {
            const nextStepId = template.steps[currentIndex + 1].id;
            updateStepState(nextStepId, {
                status: 'active',
                startTime: Date.now()
            });
            setActiveStep(currentIndex + 1);
        } else {
            // Workflow completed - show success modal instead of auto-completing
            // CRITICAL FIX: Include the current step's outputs in the final results
            const allOutputs = Object.keys(stepStates).reduce((acc, stepId) => {
                if (stepStates[stepId].outputs) {
                    acc[stepId] = stepStates[stepId].outputs!;
                }
                return acc;
            }, {} as { [stepId: string]: any });

            // CRITICAL FIX: Ensure the current step (which just completed) is included
            if (outputs && stepId) {
                allOutputs[stepId] = outputs;
                logger.log('✅ Added current step outputs to final results:', stepId, outputs);
            }

            logger.log('🎯 Final workflow results with all steps:', Object.keys(allOutputs));

            // Store results for success modal
            setWorkflowResults(allOutputs);
            setShowSuccessModal(true);

            // Clear workflow context when completed
            try {
                sessionStorage.removeItem('previousStepOutputs');
                sessionStorage.removeItem('workflowStepData');
                logger.log('Cleared workflow context - workflow completed');
            } catch (error) {
                logger.error('Error clearing workflow context:', error);
            }

            // Don't call onComplete immediately - let user close modal manually
        }
    };

    // Handle step error
    const handleStepError = (stepId: string, error: string) => {
        updateStepState(stepId, {
            status: 'error',
            error,
            endTime: Date.now()
        });
    };

    // Execute step
    const executeStep = async (step: WorkflowStep) => {
        try {
            logger.log('🚀 Starting step execution:', step.id, step.type);

            updateStepState(step.id, {
                status: 'active',
                startTime: Date.now()
            });

            // Validate step inputs
            const previousOutputs = Object.keys(stepStates).reduce((acc, stepId) => {
                if (stepStates[stepId].outputs) {
                    acc[stepId] = stepStates[stepId].outputs!;
                }
                return acc;
            }, {} as { [stepId: string]: any });

            logger.log('📊 Previous outputs for validation:', previousOutputs);

            const validation = validateWorkflowStep(step, previousOutputs);
            logger.log('✅ Step validation result:', validation);

            if (!validation.isValid) {
                throw new Error(`Step validation failed: ${validation.errors.join(', ')}`);
            }

            // Resolve inputs
            const resolvedInputs = resolveWorkflowInputs(
                step,
                previousOutputs,
                userInputs[step.id] || {}
            );

            logger.log('🔧 Resolved inputs:', resolvedInputs);

            // Execute based on step type and step ID for the new 4-step workflow
            switch (step.type) {
                case 'function_builder':
                    if (step.id === 'step1_function_builder') {
                        logger.log('🏗️ Executing Step 1: Memer function builder');
                        await executeFunctionBuilderStep(step, resolvedInputs);
                    } else if (step.id === 'step3a_multisig_selection') {
                        logger.log('🔐 Executing Step 3a: MultiSig selection');
                        await executeMultiSigSelectionStep(step, resolvedInputs);
                    } else if (step.id === 'step3b_multisig_build') {
                        logger.log('🏗️ Executing Step 3b: MultiSig build');
                        await executeMultiSigBuildStep(step, resolvedInputs);
                    } else if (step.id === 'step1_timelock_execute_builder') {
                        logger.log('⚡ Executing Step 1: Timelock execute builder');
                        await executeTimelockExecuteBuilderStep(step, resolvedInputs);
                    } else if (step.id === 'step2a_timelock_multisig_selection') {
                        logger.log('🔐 Executing Step 2a: Timelock MultiSig selection');
                        await executeTimelockMultiSigSelectionStep(step, resolvedInputs);
                    } else if (step.id === 'step2b_timelock_multisig_build') {
                        logger.log('🏗️ Executing Step 2b: Timelock MultiSig build');
                        await executeTimelockMultiSigBuildStep(step, resolvedInputs);
                    } else if (step.id === 'step2a_multisig_selection') {
                        logger.log('🔐 Executing Step 2a: Generic MultiSig selection');
                        await executeMultiSigSelectionStep(step, resolvedInputs);
                    } else if (step.id === 'step2b_multisig_execute') {
                        logger.log('🏗️ Executing Step 2b: Generic MultiSig execute');
                        await executeMultiSigBuildStep(step, resolvedInputs);
                    } else {
                        logger.log('🏗️ Executing generic function builder step');
                        await executeFunctionBuilderStep(step, resolvedInputs);
                    }
                    break;
                case 'schedule_selector':
                    logger.log('📋 Executing schedule selector step');
                    await executeScheduleSelectorStep(step, resolvedInputs);
                    break;
                case 'timelock_schedule':
                    logger.log('⏰ Executing timelock schedule step');
                    await executeTimelockScheduleStep(step, resolvedInputs);
                    break;
                case 'multisig_execute':
                    logger.log('🔐 Executing Step 4: Signature collection & execution');
                    await executeSignatureExecutionStep(step, resolvedInputs);
                    break;
                case 'timelock_execute':
                    logger.log('⚡ Executing timelock execute step');
                    await executeTimelockExecuteStep(step, resolvedInputs);
                    break;
                default:
                    throw new Error(`Unknown step type: ${step.type}`);
            }

        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : String(error);
            logger.error('❌ Step execution failed:', step.id, error);
            handleStepError(step.id, errorMessage);
        }
    };

    // Execute function builder step - REFACTORED: Simple component factory
    const executeFunctionBuilderStep = async (step: WorkflowStep, inputs: { [key: string]: string }) => {
        logger.log('🏗️ Executing function builder step (refactored):', step.id, inputs);

        // CRITICAL FIX: Handle generic timelock workflow steps properly
        if (template.id === 'generic_timelock_function') {
            logger.log('🎯 Generic timelock workflow detected, using enhanced function builder');

            // Create enhanced component for generic timelock workflow
            const GenericTimelockFunctionBuilderComponent = () => {
                const [functionBuilt, setFunctionBuilt] = useState(false);
                const [timelockKvReference, setTimelockKvReference] = useState<string>('');

                useEffect(() => {
                    // Auto-select timelock contract for generic workflow
                    const setupTimelockAutoSelection = () => {
                        try {
                            logger.log('🔍 Setting up auto-selection for generic timelock workflow...');

                            // Find the timelock contract
                            const timelockContract = contracts.find(c =>
                                c.name.toLowerCase().includes('timelock') ||
                                c.abi.some((item: any) => item.name === 'addApprovedTarget' || item.name === 'updateDelay')
                            );

                            if (timelockContract) {
                                // Store the auto-selection data for TransactionBuilder
                                const autoSelectionData = {
                                    contractAddress: timelockContract.address,
                                    contractName: timelockContract.name,
                                    functionName: '', // Let user select the function
                                    workflowContext: true,
                                    stepId: step.id,
                                    filterToContract: timelockContract.address // Filter to only show timelock functions
                                };

                                sessionStorage.setItem('workflowAutoSelection', JSON.stringify(autoSelectionData));
                                logger.log('✅ Auto-selection data stored for generic timelock step:', autoSelectionData);
                            } else {
                                logger.warn('⚠️ No timelock contract found for generic workflow!');
                            }
                        } catch (error) {
                            logger.error('❌ Error setting up generic timelock auto-selection:', error);
                        }
                    };

                    setupTimelockAutoSelection();

                    // Listen for functionBuilt events specifically for this step
                    const handleFunctionBuilt = async (event: CustomEvent) => {
                        const { functionName, functionReference, secureReference, targetContract } = event.detail;

                        logger.log('🎯 Generic timelock step received functionBuilt event:', {
                            functionName,
                            functionReference,
                            secureReference,
                            targetContract,
                            eventDetail: event.detail
                        });

                        // Check if this is a timelock function being built
                        const timelockContract = contracts.find(c =>
                            c.name.toLowerCase().includes('timelock') ||
                            c.address.toLowerCase() === targetContract?.toLowerCase()
                        );

                        if (timelockContract && targetContract?.toLowerCase() === timelockContract.address.toLowerCase()) {
                            logger.log('🎯 Timelock function built for generic workflow, capturing KV reference');

                            // Capture the KV reference from the timelock function
                            const kvRef = secureReference || functionReference;
                            setTimelockKvReference(kvRef);
                            setFunctionBuilt(true);

                            logger.log('✅ Generic timelock KV reference captured:',
                                backendDataHandler.isSecureReference(kvRef) ?
                                    backendDataHandler.formatReferenceForDisplay(kvRef) :
                                    kvRef.substring(0, 30) + '...'
                            );
                        }
                    };

                    const eventListener = (event: Event) => {
                        handleFunctionBuilt(event as CustomEvent);
                    };

                    window.addEventListener('functionBuilt', eventListener);

                    return () => {
                        window.removeEventListener('functionBuilt', eventListener);
                    };
                }, []);

                const handleContinueToMultiSig = () => {
                    logger.log('🎯 Continuing to MultiSig step with timelock KV reference');

                    // Complete this step with the timelock function data
                    completeStep(step.id, {
                        functionBuilt: true,
                        functionData: timelockKvReference,
                        targetContract: contracts.find(c => c.name.toLowerCase().includes('timelock'))?.address || '',
                        timestamp: Date.now(),
                        description: 'Generic timelock function built successfully'
                    });

                    setShowStepDialog(false);
                };

                return (
                    <Box sx={{ p: 2 }}>
                        <Alert severity="info" sx={{ mb: 2 }}>
                            <strong>Step 1: Build Timelock Function</strong>
                            <br />
                            Select and configure any timelock function (addApprovedTarget, updateDelay, etc.) for direct execution through MultiSig.
                        </Alert>

                        <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 2, mb: 2 }}>
                            <TransactionBuilder workflowContext={workflowContext} />
                        </Box>

                        <Alert severity="info" sx={{ mb: 2 }}>
                            <strong>What's Available:</strong>
                            <br />
                            • <strong>Contract:</strong> TimelockController (auto-selected)
                            <br />
                            • <strong>Functions:</strong> addApprovedTarget, updateDelay, grantRole, etc.
                            <br />
                            • <strong>Direct Execution:</strong> No scheduling delay required
                            <br />
                            <br />
                            <strong>Instructions:</strong>
                            <br />
                            1. Select your desired timelock function from the dropdown
                            <br />
                            2. Fill in the required parameters (e.g., target address for addApprovedTarget)
                            <br />
                            3. Click "Build Function" to generate the transaction
                            <br />
                            4. Click "Continue to MultiSig" when ready
                        </Alert>

                        {functionBuilt && (
                            <Alert severity="success" sx={{ mb: 2 }}>
                                <strong>✅ Timelock Function Built!</strong>
                                <br />
                                The timelock function has been successfully built. You can now continue to the MultiSig step.
                                <br />
                                <strong>KV Reference:</strong> {backendDataHandler.isSecureReference(timelockKvReference) ?
                                    backendDataHandler.formatReferenceForDisplay(timelockKvReference) :
                                    timelockKvReference.substring(0, 30) + '...'}
                            </Alert>
                        )}

                        <Box sx={{ mt: 2, display: 'flex', gap: 2, justifyContent: 'space-between' }}>
                            <Button
                                variant="outlined"
                                onClick={() => setShowStepDialog(false)}
                            >
                                Cancel Step
                            </Button>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleContinueToMultiSig}
                                disabled={!functionBuilt}
                            >
                                {functionBuilt ? 'Continue to MultiSig →' : 'Build Timelock Function First'}
                            </Button>
                        </Box>
                    </Box>
                );
            };

            setCurrentStepComponent(<GenericTimelockFunctionBuilderComponent />);
            setShowStepDialog(true);
            logger.log('🎭 Generic timelock function builder dialog opened');
            return;
        }

        // ✅ FALLBACK: Use extracted FunctionBuilderStep component for other workflows
        setCurrentStepComponent(
            <FunctionBuilderStep
                step={step}
                inputs={inputs}
                onComplete={(outputs) => {
                    logger.log('✅ FunctionBuilderStep completed with outputs:', outputs);
                    completeStep(step.id, outputs);
                    setShowStepDialog(false);
                }}
                onCancel={() => {
                    logger.log('❌ FunctionBuilderStep cancelled');
                    setShowStepDialog(false);
                }}
                workflowContext={workflowContext}
            />
        );

        setShowStepDialog(true);
        logger.log('🎭 FunctionBuilderStep dialog opened');
    };

    // Execute timelock schedule step
    const executeTimelockScheduleStep = async (step: WorkflowStep, inputs: { [key: string]: string }) => {
        logger.log('🚀 Executing timelock schedule step:', step.id, inputs);

        // Get the function data from the previous step (keep as secure reference)
        const previousStep = stepStates['step1_function_builder'];
        const functionData = previousStep?.outputs?.functionData || '0x';
        const targetContract = previousStep?.outputs?.targetContract || '';

        // Check if functionData is a secure reference using the new backend data handler
        const isSecureReference = functionData && backendDataHandler.isSecureReference(functionData);

        logger.log('📊 Previous step data:', {
            functionData: isSecureReference ? backendDataHandler.formatReferenceForDisplay(functionData) : functionData.substring(0, 20) + '...',
            targetContract,
            isSecureReference
        });

        // Store the data type information for UI display
        const functionDataType = {
            data: isSecureReference ? backendDataHandler.formatReferenceForDisplay(functionData) : functionData.substring(0, 20) + '...',
            isSecureReference
        };
        logger.log('🔐 Function data type:', functionDataType);

        // Auto-select timelock contract and schedule function
        const setupTimelockStep = () => {
            try {
                logger.log('🔍 Looking for timelock contract in contracts list...');
                logger.log('📋 Available contracts:', contracts.map(c => ({ name: c.name, address: c.address.substring(0, 10) + '...' })));

                // Find the timelock contract from deployment details
                const timelockContract = contracts.find(c =>
                    c.name.toLowerCase().includes('timelock') ||
                    c.abi.some((item: any) => item.name === 'schedule')
                );

                if (timelockContract) {
                    // Store the auto-selection data for TransactionBuilder to pick up
                    const autoSelectionData = {
                        contractAddress: timelockContract.address,
                        contractName: timelockContract.name,
                        functionName: 'schedule',
                        workflowContext: true,
                        stepId: step.id,
                        // Include resolved salt and delay from user inputs
                        resolvedInputs: inputs
                    };

                    sessionStorage.setItem('workflowAutoSelection', JSON.stringify(autoSelectionData));
                    logger.log('✅ Auto-selection data stored for timelock step:', autoSelectionData);
                    logger.log('🔧 Including resolved inputs (salt/delay):', inputs);
                } else {
                    logger.warn('⚠️ No timelock contract found in contracts list!');
                }
            } catch (error) {
                logger.error('❌ Error setting up timelock auto-selection:', error);
            }
        };

        // Setup auto-selection immediately
        setupTimelockStep();

        // Show the step dialog with embedded TransactionBuilder
        logger.log('🎭 Setting up Step 2 dialog component...');

        // Create the Step 2 component with function built detection (same pattern as Step 1)
        const TimelockScheduleComponent = () => {
            const [functionBuilt, setFunctionBuilt] = useState(false);
            const [newTimelockKvReference, setNewTimelockKvReference] = useState<string>('');

            useEffect(() => {
                // Listen for functionBuilt events specifically for this step
                const handleFunctionBuilt = async (event: CustomEvent) => {
                    const { functionName, functionReference, secureReference } = event.detail;

                    logger.log('🎯 Step 2b received functionBuilt event:', {
                        functionName,
                        functionReference,
                        secureReference,
                        eventDetail: event.detail
                    });

                    // Check if this is the timelock.schedule function being built
                    if (functionName === 'schedule') {
                        logger.log('🎯 Timelock schedule function built, capturing NEW KV reference');

                        // CRITICAL FIX: Capture the NEW KV reference from the timelock.schedule function
                        const newKvRef = secureReference || functionReference;
                        setNewTimelockKvReference(newKvRef);
                        setFunctionBuilt(true);

                        logger.log('✅ NEW timelock.schedule KV reference captured:',
                            backendDataHandler.isSecureReference(newKvRef) ?
                                backendDataHandler.formatReferenceForDisplay(newKvRef) :
                                newKvRef.substring(0, 30) + '...'
                        );

                        // UNIVERSAL PATTERN: PHASE 4.1 - Create workflow completion record immediately for SCHEDULE workflow
                        logger.log('🎯 UNIVERSAL PHASE 4.1: Creating schedule workflow completion record at Build Function time...');

                        try {
                            // Generate unique workflow ID for schedule workflow
                            const scheduleWorkflowId = `schedule_workflow_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

                            // Get salt and delay from the current form inputs (they should be available now)
                            const currentSalt = inputs.salt || sessionStorage.getItem('lastGeneratedSalt') || '';
                            const currentDelay = inputs.delay || '900';

                            logger.log('🔍 PHASE 4.1: Capturing tier1 parameters from current inputs:', {
                                salt: currentSalt?.substring(0, 20) + '...',
                                delay: currentDelay,
                                target: targetContract,
                                data: functionData
                            });

                            // Create schedule workflow completion data with ACTUAL parameters from UI form
                            const scheduleCompletionData: WorkflowCompletionData = {
                                workflowId: scheduleWorkflowId,
                                workflowName: 'Memer Function → Timelock → MultiSig',
                                transactionHash: '', // Will be filled in Phase 4.2
                                blockNumber: 0, // Will be filled in Phase 4.2
                                gasUsed: '0', // Will be filled in Phase 4.2
                                scheduleData: {
                                    target: targetContract,
                                    value: '0',
                                    data: functionData, // Original memer function KV reference
                                    predecessor: '0x0000000000000000000000000000000000000000000000000000000000000000',
                                    salt: currentSalt, // ACTUAL salt from UI form
                                    delay: currentDelay // ACTUAL delay from UI form
                                },
                                executionTimestamp: Date.now(),
                                status: '', // FIXED: Start with empty status, will be set to 'scheduled' in Phase 4.2
                                multiSigAddress: '', // Will be filled in Phase 4.2
                                functionName: 'schedule',
                                originalFunctionData: newKvRef, // Schedule function KV reference
                                operationId: '', // Will be computed by workflowCompletionService from sessionStorage
                                debugInfo: {
                                    saltCaptureMethod: 'ui_form_inputs_phase_4_1',
                                    parameterSources: {
                                        target: 'step1_function_builder',
                                        value: 'default_zero',
                                        data: 'step1_function_builder_memer_function',
                                        predecessor: 'default_zero_hash',
                                        salt: 'ui_form_salt_generator',
                                        delay: 'ui_form_delay_helper'
                                    },
                                    kvReferences: [
                                        functionData, // Original memer function KV ref
                                        newKvRef // Schedule function KV ref
                                    ].filter(Boolean),
                                    workflowSteps: ['step1_function_builder', 'step2_timelock_schedule_building'],
                                    willUpdateAfterTransaction: true // Will be updated in Phase 4.2
                                }
                            };

                            logger.log('💾 UNIVERSAL PHASE 4.1: Schedule workflow completion data:', {
                                workflowId: scheduleCompletionData.workflowId,
                                saltValue: scheduleCompletionData.scheduleData.salt?.substring(0, 20) + '...',
                                delayValue: scheduleCompletionData.scheduleData.delay,
                                status: scheduleCompletionData.status,
                                target: scheduleCompletionData.scheduleData.target
                            });

                            // Store the early schedule workflow completion record using ENHANCED method with tier1/tier2 structure
                            logger.log('🔍 UNIVERSAL PHASE 4.1: About to call storeEnhancedWorkflowCompletion with data:', {
                                workflowId: scheduleCompletionData.workflowId,
                                saltValue: scheduleCompletionData.scheduleData.salt?.substring(0, 20) + '...',
                                dataKeys: Object.keys(scheduleCompletionData)
                            });

                            const storeResult = await workflowCompletionService.storeEnhancedWorkflowCompletion(scheduleCompletionData, signer?.provider);

                            logger.log('🔍 UNIVERSAL PHASE 4.1: storeEnhancedWorkflowCompletion returned:', {
                                success: storeResult.success,
                                id: storeResult.id,
                                error: storeResult.error,
                                fullResult: storeResult
                            });

                            if (storeResult.success && storeResult.id) {
                                logger.log('✅ UNIVERSAL PHASE 4.1: Schedule workflow completion record created successfully:', storeResult.id);

                                // Store the completion ID for later update in Step 3b
                                sessionStorage.setItem('earlyWorkflowCompletionId', storeResult.id);
                                sessionStorage.setItem('earlyWorkflowCompletionData', JSON.stringify(scheduleCompletionData));

                            } else {
                                logger.error('❌ UNIVERSAL PHASE 4.1: Failed to create schedule workflow completion record:', {
                                    error: storeResult.error,
                                    success: storeResult.success,
                                    id: storeResult.id,
                                    fullResult: storeResult
                                });

                                // Store in sessionStorage as fallback
                                sessionStorage.setItem('earlyWorkflowCompletionData', JSON.stringify(scheduleCompletionData));
                                logger.log('💾 UNIVERSAL PHASE 4.1: Stored schedule completion data in sessionStorage as fallback');
                            }

                        } catch (error) {
                            logger.error('❌ UNIVERSAL PHASE 4.1: Error creating schedule workflow completion record:', error);
                            // Don't fail the function building if workflow record creation fails
                        }
                    }
                };

                const eventListener = (event: Event) => {
                    handleFunctionBuilt(event as CustomEvent);
                };

                window.addEventListener('functionBuilt', eventListener);

                return () => {
                    window.removeEventListener('functionBuilt', eventListener);
                };
            }, []);

            const handleContinueToNextStep = () => {
                logger.log('🎯 Continuing to Step 3a with NEW timelock schedule KV reference');

                // CRITICAL FIX: Store the NEW KV reference, not the old Step 1 data
                completeStep(step.id, {
                    functionBuilt: true,
                    target: targetContract,
                    functionData: newTimelockKvReference, // NEW KV reference from timelock.schedule
                    originalFunctionData: functionData, // Keep original Step 1 reference for reference
                    timestamp: Date.now(),
                    description: 'Timelock schedule function built successfully with NEW KV reference'
                });

                setShowStepDialog(false);
            };

            return (
                <Box sx={{ p: 2 }}>
                    <Alert severity="info" sx={{ mb: 2 }}>
                        <strong>Step 2: Schedule in Timelock</strong>
                        <br />
                        Build the timelock schedule function call. All parameters from Step 1 will be automatically populated.
                    </Alert>

                    {/* Show data from previous step */}
                    <Alert severity="success" sx={{ mb: 2 }}>
                        <strong>Data from Step 1:</strong>
                        <br />
                        <strong>Target Contract:</strong> {targetContract}
                        <br />
                        <strong>Function Data:</strong> {functionData.substring(0, 20)}...{functionData.substring(functionData.length - 10)}
                    </Alert>

                    <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 2, mb: 2 }}>
                        {(() => {
                            logger.log('🏗️ Rendering TransactionBuilder component in dialog...');
                            return <TransactionBuilder workflowContext={workflowContext} />;
                        })()}
                    </Box>

                    <Alert severity="info" sx={{ mb: 2 }}>
                        <strong>What's Auto-filled:</strong>
                        <br />
                        • <strong>Target:</strong> Contract address from Step 1
                        <br />
                        • <strong>Value:</strong> 0 (no ETH transfer)
                        <br />
                        • <strong>Data:</strong> Encrypted function data from Step 1
                        <br />
                        • <strong>Predecessor:</strong> 0x0000... (no dependency)
                        <br />
                        <br />
                        <strong>You Need to Generate:</strong>
                        <br />
                        • <strong>Salt:</strong> Use the Generate Salt button
                        <br />
                        • <strong>Delay:</strong> Use the Get Min Delay button (or enter manually)
                    </Alert>

                    {functionBuilt && (
                        <Alert severity="success" sx={{ mb: 2 }}>
                            <strong>✅ Schedule Function Built!</strong>
                            <br />
                            The timelock.schedule function has been successfully built. You can now continue to the next step.
                        </Alert>
                    )}

                    <Box sx={{ mt: 2, display: 'flex', gap: 2, justifyContent: 'space-between' }}>
                        <Button
                            variant="outlined"
                            onClick={() => setShowStepDialog(false)}
                        >
                            Cancel Step
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleContinueToNextStep}
                            disabled={!functionBuilt}
                        >
                            {functionBuilt ? 'Proceed to Next Step' : 'Build Schedule Function First'}
                        </Button>
                    </Box>
                </Box>
            );
        };

        setCurrentStepComponent(<TimelockScheduleComponent />);
        setShowStepDialog(true);
    };

    // Execute multisig step - Enhanced with proper MultiSig setup and signature collection
    const executeMultiSigStep = async (step: WorkflowStep, inputs: { [key: string]: string }) => {
        logger.log('🔐 Executing multisig step:', step.id, inputs);

        // Get data from previous steps
        const timelockScheduleStep = stepStates['step2_timelock_schedule'];
        const timelockController = timelockScheduleStep?.outputs?.target || '';
        const encodedFunctionData = timelockScheduleStep?.outputs?.functionData || '0x';

        logger.log('📊 Previous step data for MultiSig:', {
            timelockController,
            encodedFunctionData: encodedFunctionData.substring(0, 20) + '...',
        });

        // Step 3a: MultiSig Setup Component
        const MultiSigSetupComponent = () => {
            const [selectedMultiSig, setSelectedMultiSig] = useState<string>('');
            const [selectedFunction, setSelectedFunction] = useState<string>('execTransaction');
            const [setupComplete, setSetupComplete] = useState(false);

            // Find MultiSig contracts
            const multiSigContracts = contracts.filter(c =>
                c.name.toLowerCase().includes('multisig') ||
                c.abi.some((item: any) => item.name === 'execTransaction')
            );

            const handleSetupComplete = () => {
                if (!selectedMultiSig) {
                    alert('Please select a MultiSig wallet');
                    return;
                }

                // Store setup data for Step 3b
                const multiSigSetupData = {
                    multiSigAddress: selectedMultiSig,
                    functionName: selectedFunction,
                    // Pre-filled parameters based on your specifications
                    to: timelockController, // The timelock controller from Step 2
                    data: encodedFunctionData, // Encrypted function data from Step 2
                    refundReceiver: selectedMultiSig, // The chosen multisig wallet
                    // Preset values as specified
                    value: '0',
                    operation: '0', // Standard call operation
                    safeTxGas: '0',
                    baseGas: '0',
                    gasPrice: '0',
                    gasToken: '0x0000000000000000000000000000000000000000',
                    signatures: '0x' // Placeholder for signature collection
                };

                sessionStorage.setItem('multiSigSetupData', JSON.stringify(multiSigSetupData));
                logger.log('✅ MultiSig setup data stored:', multiSigSetupData);
                setSetupComplete(true);
            };

            if (setupComplete) {
                return <MultiSigExecutionComponent />;
            }

            return (
                <Box sx={{ p: 2 }}>
                    <Alert severity="info" sx={{ mb: 2 }}>
                        <strong>Step 3a: MultiSig Setup</strong>
                        <br />
                        Choose your MultiSig wallet and execution function. Parameters will be auto-filled from previous steps.
                    </Alert>

                    {/* MultiSig Selection */}
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle1" gutterBottom>
                            1. Select MultiSig Wallet:
                        </Typography>
                        <AddressSelector
                            value={selectedMultiSig}
                            onChange={setSelectedMultiSig}
                            contracts={multiSigContracts}
                            label="MultiSig Wallet"
                            placeholder="Select your MultiSig wallet"
                            helperText="Choose the MultiSig wallet that will execute the timelock operation"
                        />
                    </Box>

                    {/* Function Selection */}
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle1" gutterBottom>
                            2. Select Execution Function:
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <Button
                                variant={selectedFunction === 'execTransaction' ? 'contained' : 'outlined'}
                                onClick={() => setSelectedFunction('execTransaction')}
                            >
                                execTransaction
                            </Button>
                            <Button
                                variant={selectedFunction === 'executeTransactionWithChecks' ? 'contained' : 'outlined'}
                                onClick={() => setSelectedFunction('executeTransactionWithChecks')}
                            >
                                executeTransactionWithChecks
                            </Button>
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                            {selectedFunction === 'execTransaction'
                                ? 'Standard execution without additional checks'
                                : 'Execution with additional safety checks'
                            }
                        </Typography>
                    </Box>

                    {/* Parameter Preview */}
                    <Alert severity="success" sx={{ mb: 2 }}>
                        <strong>Auto-filled Parameters Preview:</strong>
                        <br />
                        <strong>To:</strong> {timelockController} (Timelock Controller)
                        <br />
                        <strong>Data:</strong> {encodedFunctionData.substring(0, 30)}... (Encrypted function data)
                        <br />
                        <strong>RefundReceiver:</strong> {selectedMultiSig || '[Selected MultiSig]'}
                        <br />
                        <strong>Value:</strong> 0, <strong>Operation:</strong> 0, <strong>Gas params:</strong> 0
                        <br />
                        <strong>GasToken:</strong> 0x0000...0000, <strong>Signatures:</strong> 0x (placeholder)
                    </Alert>

                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'space-between' }}>
                        <Button
                            variant="outlined"
                            onClick={() => setShowStepDialog(false)}
                        >
                            Cancel Step
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleSetupComplete}
                            disabled={!selectedMultiSig}
                        >
                            Continue to Signature Collection →
                        </Button>
                    </Box>
                </Box>
            );
        };

        // Step 3b: Signature Collection & Execution Component
        const MultiSigExecutionComponent = () => {
            const signatureContext = useSignatureContext();
            const [executionComplete, setExecutionComplete] = useState(false);

            // Load setup data
            const setupData = JSON.parse(sessionStorage.getItem('multiSigSetupData') || '{}');

            useEffect(() => {
                // Setup auto-selection for TransactionBuilder (similar to Step 2)
                const autoSelectionData = {
                    contractAddress: setupData.multiSigAddress,
                    contractName: 'MultiSig Wallet',
                    functionName: setupData.functionName,
                    workflowContext: true,
                    stepId: step.id,
                    // Pre-populate all the MultiSig function parameters
                    prefilledInputs: {
                        to: setupData.to,
                        value: setupData.value,
                        data: setupData.data,
                        operation: setupData.operation,
                        safeTxGas: setupData.safeTxGas,
                        baseGas: setupData.baseGas,
                        gasPrice: setupData.gasPrice,
                        gasToken: setupData.gasToken,
                        refundReceiver: setupData.refundReceiver,
                        signatures: setupData.signatures
                    }
                };

                sessionStorage.setItem('workflowAutoSelection', JSON.stringify(autoSelectionData));
                logger.log('✅ Auto-selection data stored for MultiSig step:', autoSelectionData);

                // Initialize signature context with transaction data
                signatureContext.setTransactionData({
                    inputs: [
                        setupData.to,
                        setupData.value,
                        setupData.data,
                        setupData.operation,
                        setupData.safeTxGas,
                        setupData.baseGas,
                        setupData.gasPrice,
                        setupData.gasToken,
                        setupData.refundReceiver,
                        setupData.signatures
                    ],
                    contract: setupData.multiSigAddress,
                    functionName: setupData.functionName
                });

                // Mock MultiSig owners for demonstration
                const mockOwners = [
                    '0x1234567890123456789012345678901234567890',
                    '0x2345678901234567890123456789012345678901',
                    '0x3456789012345678901234567890123456789012'
                ];

                signatureContext.updateSignatureProgress({
                    requiredCount: 2, // 2 of 3 multisig
                    owners: mockOwners,
                    collectedCount: 0,
                    completedSigners: []
                });
            }, []);

            const handleExecuteTransaction = () => {
                // In real implementation, this would execute the MultiSig transaction
                const mockExecutionTxHash = '0x' + Math.random().toString(16).substr(2, 64);

                completeStep(step.id, {
                    executionTxHash: mockExecutionTxHash,
                    multiSigAddress: setupData.multiSigAddress,
                    functionName: setupData.functionName,
                    finalTarget: setupData.to,
                    finalFunctionData: setupData.data,
                    executedAt: Date.now(),
                    signatures: signatureContext.getAllSignatures(),
                    description: 'MultiSig transaction executed successfully'
                });
                setShowStepDialog(false);
            };

            return (
                <Box sx={{ p: 2 }}>
                    <Alert severity="info" sx={{ mb: 2 }}>
                        <strong>Step 3b: Build MultiSig Transaction</strong>
                        <br />
                        Review and build the MultiSig transaction with pre-filled parameters. All values from previous steps are populated but can be edited if needed.
                    </Alert>

                    {/* Transaction Builder with pre-filled values */}
                    <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 2, mb: 2 }}>
                        <TransactionBuilder workflowContext={workflowContext} />
                    </Box>

                    <Alert severity="info" sx={{ mb: 2 }}>
                        <strong>What's Pre-filled:</strong>
                        <br />
                        • <strong>To:</strong> {setupData.to} (Timelock Controller)
                        <br />
                        • <strong>Value:</strong> {setupData.value} (no ETH transfer)
                        <br />
                        • <strong>Data:</strong> {setupData.data?.substring(0, 30)}... (Encrypted function data)
                        <br />
                        • <strong>Operation:</strong> {setupData.operation} (standard call)
                        <br />
                        • <strong>RefundReceiver:</strong> {setupData.refundReceiver} (selected MultiSig)
                        <br />
                        • <strong>Gas Parameters:</strong> All set to 0 (let wallet estimate)
                        <br />
                        • <strong>Signatures:</strong> 0x (placeholder for collection)
                        <br />
                        <br />
                        <strong>Next Steps:</strong>
                        <br />
                        1. Review the pre-filled parameters above
                        <br />
                        2. Edit any values if needed
                        <br />
                        3. Click "Build Function" to generate the transaction
                        <br />
                        4. Use signature collection system to gather required signatures
                        <br />
                        5. Execute the final transaction
                    </Alert>

                    {/* Signature Progress Display */}
                    <SignatureProgressDisplay />

                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'space-between' }}>
                        <Button
                            variant="outlined"
                            onClick={() => {
                                signatureContext.clearAllStoredData();
                                setShowStepDialog(false);
                            }}
                        >
                            Cancel & Clear
                        </Button>
                        <Button
                            variant="contained"
                            color="success"
                            onClick={handleExecuteTransaction}
                            disabled={signatureContext.signatureProgress.collectedCount < signatureContext.signatureProgress.requiredCount}
                        >
                            Complete MultiSig Workflow ✅
                        </Button>
                    </Box>
                </Box>
            );
        };

        // Show the step dialog starting with MultiSig Setup
        setCurrentStepComponent(<MultiSigSetupComponent />);
        setShowStepDialog(true);
    };

    // Execute Step 3a: MultiSig Selection
    const executeMultiSigSelectionStep = async (step: WorkflowStep, inputs: { [key: string]: string }) => {
        logger.log('🔐 Executing Step 3a: MultiSig Selection', step.id, inputs);

        // Create a component that captures the selection without building
        const MultiSigSelectionComponent = () => {
            const [selectedContract, setSelectedContract] = useState<any>(null);
            const [selectedFunction, setSelectedFunction] = useState<string>('');
            const [selectionComplete, setSelectionComplete] = useState(false);

            // Find MultiSig contracts
            const multiSigContracts = contracts.filter(c =>
                c.name.toLowerCase().includes('multisig') ||
                c.abi.some((item: any) => item.name === 'execTransaction')
            );

            // Listen for contract and function selection from TransactionBuilder
            useEffect(() => {
                const handleContractSelection = () => {
                    // Check if both contract and function are selected
                    if (selectedContract && selectedFunction) {
                        setSelectionComplete(true);
                    }
                };

                handleContractSelection();
            }, [selectedContract, selectedFunction]);

            const handleContinue = () => {
                if (!selectedContract || !selectedFunction) {
                    alert('Please select both a MultiSig contract and function');
                    return;
                }

                logger.log('✅ Capturing Step 3a selection:', {
                    contract: selectedContract.name,
                    address: selectedContract.address,
                    function: selectedFunction
                });

                completeStep(step.id, {
                    multiSigAddress: selectedContract.address,
                    functionName: selectedFunction,
                    contractName: selectedContract.name,
                    description: `Selected ${selectedContract.name}.${selectedFunction}`
                });
                setShowStepDialog(false);
            };

            return (
                <Box sx={{ p: 2 }}>
                    <Alert severity="info" sx={{ mb: 2 }}>
                        <strong>Step 3a: Choose MultiSig & Function</strong>
                        <br />
                        Select your MultiSig wallet and execution function. Parameters will be auto-filled in the next step.
                    </Alert>

                    {/* Contract Selection */}
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle1" gutterBottom>
                            1. Select MultiSig Wallet:
                        </Typography>
                        <AddressSelector
                            value={selectedContract?.address || ''}
                            onChange={(address) => {
                                const contract = multiSigContracts.find(c => c.address === address);
                                setSelectedContract(contract || null);
                                logger.log('📋 Contract selected:', contract?.name);
                            }}
                            contracts={multiSigContracts}
                            label="MultiSig Wallet"
                            placeholder="Select your MultiSig wallet"
                            helperText="Choose the MultiSig wallet that will execute the timelock operation"
                        />
                    </Box>

                    {/* Function Selection */}
                    {selectedContract && (
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="subtitle1" gutterBottom>
                                2. Select Execution Function:
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <Button
                                    variant={selectedFunction === 'execTransaction' ? 'contained' : 'outlined'}
                                    onClick={() => {
                                        setSelectedFunction('execTransaction');
                                        logger.log('🔧 Function selected: execTransaction');
                                    }}
                                >
                                    execTransaction
                                </Button>
                                <Button
                                    variant={selectedFunction === 'executeTransactionWithChecks' ? 'contained' : 'outlined'}
                                    onClick={() => {
                                        setSelectedFunction('executeTransactionWithChecks');
                                        logger.log('🔧 Function selected: executeTransactionWithChecks');
                                    }}
                                >
                                    executeTransactionWithChecks
                                </Button>
                            </Box>
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                {selectedFunction === 'execTransaction'
                                    ? 'Standard execution without additional checks'
                                    : selectedFunction === 'executeTransactionWithChecks'
                                        ? 'Execution with additional safety checks'
                                        : 'Choose your preferred execution method'
                                }
                            </Typography>
                        </Box>
                    )}

                    {/* Selection Summary */}
                    {selectedContract && selectedFunction && (
                        <Alert severity="success" sx={{ mb: 2 }}>
                            <strong>Selection Summary:</strong>
                            <br />
                            <strong>MultiSig:</strong> {selectedContract.name}
                            <br />
                            <strong>Address:</strong> {selectedContract.address}
                            <br />
                            <strong>Function:</strong> {selectedFunction}
                            <br />
                            <br />
                            <strong>Next Step:</strong> Parameters will be auto-filled from previous steps
                        </Alert>
                    )}

                    <Box sx={{ mt: 2, display: 'flex', gap: 2, justifyContent: 'space-between' }}>
                        <Button
                            variant="outlined"
                            onClick={() => setShowStepDialog(false)}
                        >
                            Cancel Step
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleContinue}
                            disabled={!selectedContract || !selectedFunction}
                        >
                            Continue to Build Transaction →
                        </Button>
                    </Box>
                </Box>
            );
        };

        setCurrentStepComponent(<MultiSigSelectionComponent />);
        setShowStepDialog(true);
    };

    // Execute Step 3b: MultiSig Build
    const executeMultiSigBuildStep = async (step: WorkflowStep, inputs: { [key: string]: string }) => {
        logger.log('🏗️ Executing MultiSig Build Step:', step.id, inputs);

        // UNIVERSAL FIX: Handle different workflow step naming patterns
        let step2aData, step1Data, timelockData;
        let selectedMultiSig = '';
        let selectedFunction = 'execTransaction';
        let functionDataReference = '0x';
        let targetContract = '';

        // Determine workflow type and get appropriate data
        if (template.id === 'generic_timelock_function') {
            // Generic timelock workflow: step1_timelock_function_builder -> step2a_multisig_selection -> step2b_multisig_execute
            step2aData = stepStates['step2a_multisig_selection'];
            step1Data = stepStates['step1_timelock_function_builder'];

            selectedMultiSig = step2aData?.outputs?.multiSigAddress || '';
            selectedFunction = step2aData?.outputs?.functionName || 'execTransaction';
            functionDataReference = step1Data?.outputs?.functionData || '0x'; // Timelock function KV reference

            // Target is the timelock controller (direct execution)
            const timelockContract = contracts.find(c =>
                c.name.toLowerCase().includes('timelock') ||
                c.abi.some((item: any) => item.name === 'addApprovedTarget')
            );
            targetContract = timelockContract?.address || '';

            logger.log('📊 Generic timelock workflow data:', {
                step2aData: step2aData?.outputs,
                step1Data: step1Data?.outputs,
                selectedMultiSig,
                selectedFunction,
                functionDataReference: functionDataReference.substring(0, 30) + '...',
                targetContract,
                dataSource: 'Direct timelock execution'
            });

        } else {
            // Complex workflows: step3a_multisig_selection -> step3b_multisig_build
            step2aData = stepStates['step3a_multisig_selection'];
            timelockData = stepStates['step2_timelock_schedule'];
            step1Data = stepStates['step1_function_builder'];

            selectedMultiSig = step2aData?.outputs?.multiSigAddress || '';
            selectedFunction = step2aData?.outputs?.functionName || 'execTransaction';

            // Get timelock controller address from contracts
            const timelockContract = contracts.find(c =>
                c.name.toLowerCase().includes('timelock') ||
                c.abi.some((item: any) => item.name === 'schedule')
            );
            targetContract = timelockContract?.address || '';

            // Use the NEW KV reference from Step 2b (timelock.schedule), not Step 1
            functionDataReference = timelockData?.outputs?.functionData || '0x';
            const originalStep1Reference = timelockData?.outputs?.originalFunctionData || step1Data?.outputs?.functionData || '0x';
            const isNewKvReference = functionDataReference && backendDataHandler.isSecureReference(functionDataReference);

            logger.log('📊 Complex workflow data:', {
                step2aData: step2aData?.outputs,
                timelockData: timelockData?.outputs,
                step1Data: step1Data?.outputs,
                selectedMultiSig,
                selectedFunction,
                targetContract,
                functionDataReference: isNewKvReference ? backendDataHandler.formatReferenceForDisplay(functionDataReference) : functionDataReference.substring(0, 30) + '...',
                originalStep1Reference: originalStep1Reference.substring(0, 30) + '...',
                isNewKvReference,
                dataSource: 'Using NEW timelock.schedule KV reference from Step 2b'
            });
        }

        // Create the Step 3b component
        const MultiSigBuildComponent = () => {
            const [autoSelectionSet, setAutoSelectionSet] = useState(false);

            useEffect(() => {
                // Clear any existing auto-selection data first
                sessionStorage.removeItem('workflowAutoSelection');

                // Small delay to ensure clean state
                setTimeout(() => {
                    // Setup auto-selection for TransactionBuilder
                    const autoSelectionData = {
                        contractAddress: selectedMultiSig,
                        contractName: 'MultiSig Wallet',
                        functionName: selectedFunction,
                        workflowContext: true,
                        stepId: step.id,
                        prefilledInputs: {
                            to: targetContract,
                            value: '0',
                            data: functionDataReference,
                            operation: '0',
                            safeTxGas: '0',
                            baseGas: '0',
                            gasPrice: '0',
                            gasToken: '0x0000000000000000000000000000000000000000',
                            refundReceiver: selectedMultiSig,
                            signatures: '0x'
                        }
                    };

                    sessionStorage.setItem('workflowAutoSelection', JSON.stringify(autoSelectionData));
                    logger.log('✅ Auto-selection data stored for Step 3b:', autoSelectionData);

                    // Force a small delay before showing TransactionBuilder to ensure it picks up the data
                    setTimeout(() => {
                        setAutoSelectionSet(true);
                    }, 100);
                }, 50);
            }, []);

            return (
                <Box sx={{ p: 2 }}>
                    <Alert severity="info" sx={{ mb: 2 }}>
                        <strong>Step 3b: Build MultiSig Transaction</strong>
                        <br />
                        Build the MultiSig transaction with auto-filled parameters from previous steps. All values are pre-populated but editable.
                    </Alert>

                    {/* Show selection from Step 3a */}
                    <Alert severity="success" sx={{ mb: 2 }}>
                        <strong>From Step 2a:</strong>
                        <br />
                        <strong>MultiSig:</strong> {step2aData?.outputs?.contractName || 'MultiSig Wallet'}
                        <br />
                        <strong>Address:</strong> {selectedMultiSig}
                        <br />
                        <strong>Function:</strong> {selectedFunction}
                    </Alert>

                    {/* Debug info */}
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        <strong>Debug Info:</strong>
                        <br />
                        Auto-selection set: {autoSelectionSet ? 'Yes' : 'No'}
                        <br />
                        SessionStorage key exists: {sessionStorage.getItem('workflowAutoSelection') ? 'Yes' : 'No'}
                        <br />
                        Data preview: {sessionStorage.getItem('workflowAutoSelection')?.substring(0, 100)}...
                    </Alert>

                    {/* FunctionExplorer with auto-selection and transaction completion callback */}
                    {autoSelectionSet && (
                        <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 2, mb: 2 }}>
                            <FunctionExplorer
                                key={`fe-${selectedMultiSig}-${selectedFunction}-${Date.now()}`}
                                workflowContext={workflowContext}
                                onTransactionComplete={async (transactionHash: string, receipt: any, executionContext: any) => {
                                    logger.log('🎯 Workflow: Transaction completed in FunctionExplorer!', {
                                        transactionHash,
                                        blockNumber: receipt.blockNumber,
                                        gasUsed: receipt.gasUsed.toString()
                                    });

                                    try {
                                        // PHASE 4.2: UPDATE EXISTING WORKFLOW RECORD with execution details
                                        logger.log('🎯 PHASE 4.2: Updating existing workflow completion record with execution details...');

                                        // Check if we have an early workflow completion ID from Phase 4.1
                                        const earlyCompletionId = sessionStorage.getItem('earlyWorkflowCompletionId');
                                        const earlyCompletionData = sessionStorage.getItem('earlyWorkflowCompletionData');

                                        if (earlyCompletionId && earlyCompletionData) {
                                            logger.log('✅ PHASE 4.2: Found early workflow completion record to update:', earlyCompletionId);

                                            try {
                                                const parsedEarlyData = JSON.parse(earlyCompletionData);

                                                // Create updated completion data with execution details
                                                const updatedCompletionData: WorkflowCompletionData = {
                                                    ...parsedEarlyData,
                                                    transactionHash, // Add actual transaction hash
                                                    blockNumber: receipt.blockNumber, // Add block number
                                                    gasUsed: receipt.gasUsed.toString(), // Add gas used
                                                    status: 'scheduled', // FIXED: Update status from '' to 'scheduled' (not 'completed')
                                                    multiSigAddress: selectedMultiSig, // Add MultiSig address
                                                    executionTimestamp: Date.now(), // Update execution timestamp
                                                    debugInfo: {
                                                        ...parsedEarlyData.debugInfo,
                                                        workflowSteps: [...(parsedEarlyData.debugInfo?.workflowSteps || []), 'step3a_multisig_selection', 'step3b_multisig_build_completed'],
                                                        executionDetails: {
                                                            transactionHash,
                                                            blockNumber: receipt.blockNumber,
                                                            gasUsed: receipt.gasUsed.toString(),
                                                            multiSigAddress: selectedMultiSig,
                                                            functionName: selectedFunction,
                                                            completedAt: Date.now(),
                                                            updateMethod: 'phase_4_2_execution_update'
                                                        }
                                                    }
                                                };

                                                logger.log('💾 PHASE 4.2: Updating workflow completion record with execution details:', {
                                                    id: earlyCompletionId,
                                                    transactionHash,
                                                    blockNumber: receipt.blockNumber,
                                                    gasUsed: receipt.gasUsed.toString(),
                                                    status: 'completed'
                                                });

                                                // Update the existing record
                                                const updateResult = await workflowCompletionService.updateWorkflowCompletion(earlyCompletionId, updatedCompletionData);

                                                if (updateResult.success) {
                                                    logger.log('✅ PHASE 4.2: Successfully updated existing workflow completion record');

                                                    // Clean up sessionStorage
                                                    sessionStorage.removeItem('earlyWorkflowCompletionId');
                                                    sessionStorage.removeItem('earlyWorkflowCompletionData');
                                                    logger.log('🧹 PHASE 4.2: Cleaned up early completion sessionStorage data');
                                                } else {
                                                    logger.error('❌ PHASE 4.2: Failed to update existing workflow completion record:', updateResult.error);
                                                    throw new Error(`Failed to update workflow completion: ${updateResult.error}`);
                                                }

                                            } catch (parseError) {
                                                logger.error('❌ PHASE 4.2: Error parsing early completion data:', parseError);
                                                throw parseError;
                                            }

                                        } else {
                                            logger.log('ℹ️ PHASE 4.2: No early workflow completion record found - this should not happen in normal flow');
                                            logger.log('⚠️ PHASE 4.2: The workflow completion record should have been created in Step 2b');

                                            // CRITICAL FIX: Don't create a new record that would overwrite operationId
                                            // The backend already has the correct record with operationId from Step 2b
                                            // Just complete the step without creating duplicate records
                                            logger.log('✅ PHASE 4.2: Completing step without creating duplicate workflow record');
                                        }

                                        // Complete the workflow step with comprehensive data
                                        completeStep(step.id, {
                                            executionTxHash: transactionHash,
                                            blockNumber: receipt.blockNumber,
                                            gasUsed: receipt.gasUsed.toString(),
                                            multiSigAddress: selectedMultiSig,
                                            functionName: selectedFunction,
                                            completionId: earlyCompletionId,
                                            originalFunctionData: step1Data?.outputs?.functionData,
                                            description: `✅ Workflow completed! Transaction: ${transactionHash}`
                                        });

                                        // Close the dialog
                                        setShowStepDialog(false);

                                    } catch (error) {
                                        logger.error('❌ Error handling transaction completion:', error);
                                        // Still complete the step even if storage fails
                                        completeStep(step.id, {
                                            executionTxHash: transactionHash,
                                            blockNumber: receipt.blockNumber,
                                            gasUsed: receipt.gasUsed.toString(),
                                            multiSigAddress: selectedMultiSig,
                                            functionName: selectedFunction,
                                            error: `Transaction completed but storage failed: ${error instanceof Error ? error.message : String(error)}`,
                                            description: `⚠️ Transaction completed with storage error: ${transactionHash}`
                                        });
                                        setShowStepDialog(false);
                                    }
                                }}
                            />
                        </Box>
                    )}

                    {!autoSelectionSet && (
                        <Box sx={{ p: 2, textAlign: 'center' }}>
                            <Typography>Setting up auto-selection...</Typography>
                        </Box>
                    )}

                    <Alert severity="info" sx={{ mb: 2 }}>
                        <strong>What's Pre-filled:</strong>
                        <br />
                        • <strong>Contract:</strong> {selectedMultiSig} (from Step 3a)
                        <br />
                        • <strong>Function:</strong> {selectedFunction} (from Step 3a)
                        <br />
                        • <strong>To:</strong> {targetContract} (Timelock Controller)
                        <br />
                        • <strong>Data:</strong> {functionDataReference.substring(0, 30)}... (KV Reference ID)
                        <br />
                        • <strong>RefundReceiver:</strong> {selectedMultiSig} (selected MultiSig)
                        <br />
                        • <strong>All other parameters:</strong> Set to defaults (0, 0x0000...)
                        <br />
                        <br />
                        <strong>Instructions:</strong>
                        <br />
                        1. Review the pre-filled parameters above
                        <br />
                        2. Edit any values if needed in the form fields
                        <br />
                        3. Click "Build Function" to generate the transaction
                        <br />
                        4. Click "Capture Transaction & Continue" when ready
                    </Alert>

                    <Box sx={{ mt: 2, display: 'flex', gap: 2, justifyContent: 'space-between' }}>
                        <Button
                            variant="outlined"
                            onClick={() => setShowStepDialog(false)}
                        >
                            Cancel Step
                        </Button>
                        <Alert severity="info" sx={{ flexGrow: 1, ml: 2 }}>
                            <strong>Automatic Execution:</strong> The transaction will be executed automatically when you complete the signature collection in the FunctionExplorer above. No additional button needed!
                        </Alert>
                    </Box>
                </Box>
            );
        };

        setCurrentStepComponent(<MultiSigBuildComponent />);
        setShowStepDialog(true);
    };

    // Execute Step 4: Signature Collection & Execution
    const executeSignatureExecutionStep = async (step: WorkflowStep, inputs: { [key: string]: string }) => {
        logger.log('🔐 Executing Step 4: Signature Collection & Execution', step.id, inputs);

        // Get data from previous steps
        const step3bData = stepStates['step3b_multisig_build'];
        const step3aData = stepStates['step3a_multisig_selection'];

        const builtTransaction = step3bData?.outputs?.functionData || '0x';
        const multiSigAddress = step3aData?.outputs?.multiSigAddress || '';
        const functionName = step3aData?.outputs?.functionName || 'execTransaction';

        // Create Step 4 component with proper React hooks usage
        const SignatureExecutionComponent = () => {
            const signatureContext = useSignatureContext();

            useEffect(() => {
                // Initialize signature context
                signatureContext.setTransactionData({
                    inputs: [], // Would be populated from Step 3b
                    contract: multiSigAddress,
                    functionName: functionName
                });

                // Mock MultiSig owners
                const mockOwners = [
                    '0x1234567890123456789012345678901234567890',
                    '0x2345678901234567890123456789012345678901',
                    '0x3456789012345678901234567890123456789012'
                ];

                signatureContext.updateSignatureProgress({
                    requiredCount: 2,
                    owners: mockOwners,
                    collectedCount: 0,
                    completedSigners: []
                });
            }, []);

            return (
                <Box sx={{ p: 2 }}>
                    <Alert severity="info" sx={{ mb: 2 }}>
                        <strong>Step 4: Collect Signatures & Execute</strong>
                        <br />
                        Collect required signatures from MultiSig owners and execute the final transaction.
                    </Alert>

                    <Alert severity="success" sx={{ mb: 2 }}>
                        <strong>Transaction Ready:</strong>
                        <br />
                        <strong>MultiSig:</strong> {multiSigAddress}
                        <br />
                        <strong>Function:</strong> {functionName}
                        <br />
                        <strong>Transaction Data:</strong> {builtTransaction.substring(0, 30)}...
                    </Alert>

                    <SignatureProgressDisplay />

                    <Alert severity="warning" sx={{ mb: 2 }}>
                        <strong>Signature Collection Process:</strong>
                        <br />
                        1. Switch your wallet to each MultiSig owner account
                        <br />
                        2. Click "Collect Signature" for each owner
                        <br />
                        3. Once you have {signatureContext.signatureProgress.requiredCount} signatures, execute the transaction
                        <br />
                        4. The final transaction will execute your original Memer function
                    </Alert>

                    <Box sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                        <Typography variant="h6" gutterBottom>
                            Signature Collection
                        </Typography>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={() => {
                                // Mock signature collection
                                const mockSignature = '0x' + Math.random().toString(16).substr(2, 130);
                                const mockSigner = '0x' + Math.random().toString(16).substr(2, 40);
                                signatureContext.addSignature(mockSigner, mockSignature);
                            }}
                            disabled={signatureContext.signatureProgress.collectedCount >= signatureContext.signatureProgress.requiredCount}
                        >
                            Collect Signature from Current Wallet
                        </Button>
                    </Box>

                    <Box sx={{ mt: 2, display: 'flex', gap: 2, justifyContent: 'space-between' }}>
                        <Button
                            variant="outlined"
                            onClick={() => {
                                signatureContext.clearAllStoredData();
                                setShowStepDialog(false);
                            }}
                        >
                            Cancel & Clear
                        </Button>
                        <Button
                            variant="contained"
                            color="success"
                            onClick={() => {
                                const mockExecutionTxHash = '0x' + Math.random().toString(16).substr(2, 64);

                                completeStep(step.id, {
                                    executionTxHash: mockExecutionTxHash,
                                    signatures: signatureContext.getAllSignatures(),
                                    executedAt: Date.now(),
                                    description: 'Workflow completed successfully! Memer function executed via Timelock → MultiSig'
                                });
                                setShowStepDialog(false);
                            }}
                            disabled={signatureContext.signatureProgress.collectedCount < signatureContext.signatureProgress.requiredCount}
                        >
                            Execute Final Transaction ✅
                        </Button>
                    </Box>
                </Box>
            );
        };

        setCurrentStepComponent(<SignatureExecutionComponent />);
        setShowStepDialog(true);
    };

    // Execute schedule selector step (Step 0 of timelock_to_multisig workflow)
    const executeScheduleSelectorStep = async (step: WorkflowStep, inputs: { [key: string]: string }) => {
        logger.log('📋 Executing schedule selector step:', step.id, inputs);

        // Create the schedule selection component
        const ScheduleSelectorComponent = () => {
            const [availableSchedules, setAvailableSchedules] = useState<any[]>([]);
            const [selectedSchedule, setSelectedSchedule] = useState<any>(null);
            const [loading, setLoading] = useState(true);
            const [error, setError] = useState<string | null>(null);
            // NEW: Real-time timelock status tracking
            const [timelockStatuses, setTimelockStatuses] = useState<{
                [operationId: string]: {
                    isReady: boolean;
                    isPending: boolean;
                    isDone: boolean;
                    error?: string;
                    lastChecked: number;
                }
            }>({});
            const [statusLoading, setStatusLoading] = useState(false);

            // NEW: Get timelock contract for status checks
            const timelockContract = contracts.find(c =>
                c.name.toLowerCase().includes('timelock') ||
                c.abi.some((item: any) => item.name === 'isOperationReady')
            );

            // NEW: Batch check timelock status for all operations
            const checkTimelockStatuses = async (schedules: any[]) => {
                if (!timelockContract || !signer || schedules.length === 0) {
                    logger.log('⚠️ Cannot check timelock statuses: missing contract or signer');
                    return;
                }

                setStatusLoading(true);
                logger.log('🔍 Batch checking timelock statuses for', schedules.length, 'operations');

                try {
                    // TIMESTAMP-BASED CACHE BUSTING: Use timestamp instead of block number for forked chains
                    let freshProvider = signer?.provider;
                    if (window.ethereum && signer?.provider) {
                        try {
                            // Create completely new provider with unique timestamp-based name
                            const timestamp = Date.now();
                            freshProvider = new ethers.providers.Web3Provider(
                                window.ethereum as any,
                                {
                                    name: `fresh-timelock-${timestamp}`, // Unique timestamp prevents caching
                                    chainId: (signer.provider as any).network?.chainId || 31337,
                                    ensAddress: undefined
                                }
                            );

                            // CRITICAL: Force fresh call to ensure provider is initialized
                            await freshProvider.getNetwork();

                            logger.log(`🔄 TIMESTAMP cache bust: Fresh provider created at ${timestamp}`);
                        } catch (error) {
                            logger.warn('Failed to create fresh provider, using existing one:', error);
                            freshProvider = signer?.provider;
                        }
                    }

                    const contract = new ethers.Contract(
                        timelockContract.address,
                        timelockContract.abi,
                        freshProvider || signer
                    );
                    const statusUpdates: { [operationId: string]: any } = {};

                    // Process each schedule
                    for (const schedule of schedules) {
                        let operationId = schedule.operationId;

                        // AUTO-COMPUTE missing operationId
                        if (!operationId && schedule.scheduleData) {
                            try {
                                logger.log('🔄 Auto-computing missing operationId for schedule:', schedule.workflowName);

                                // CRITICAL FIX: Handle KV references in data field
                                let dataForComputation = schedule.scheduleData.data || '0x';

                                // Check if data is a KV reference (like {{fb_1756052399165_65e5dcb841059f2c}})
                                if (backendDataHandler.isSecureReference(dataForComputation)) {
                                    logger.log('🔍 Data field contains KV reference, attempting to retrieve actual hex data...');

                                    try {
                                        // Try to retrieve the actual hex data from the KV reference
                                        const retrievalResult = await backendDataHandler.autoDecryptIfNeeded(dataForComputation);

                                        if (retrievalResult.success && retrievalResult.data) {
                                            dataForComputation = retrievalResult.data;
                                            logger.log('✅ Retrieved actual hex data from KV reference:', dataForComputation.substring(0, 50) + '...');
                                        } else {
                                            logger.warn('⚠️ Could not retrieve data from KV reference, using placeholder');
                                            dataForComputation = '0x'; // Fallback to empty data
                                        }
                                    } catch (retrievalError) {
                                        logger.warn('⚠️ Error retrieving data from KV reference:', retrievalError);
                                        dataForComputation = '0x'; // Fallback to empty data
                                    }
                                } else {
                                    logger.log('✅ Data field contains direct hex data');
                                }

                                operationId = computeOperationId(
                                    schedule.scheduleData.target,
                                    schedule.scheduleData.value || '0',
                                    dataForComputation,
                                    schedule.scheduleData.predecessor || '0x0000000000000000000000000000000000000000000000000000000000000000',
                                    schedule.scheduleData.salt
                                );
                                logger.log('✅ Auto-computed operationId:', operationId.substring(0, 20) + '...');

                                // Update the schedule object with computed operationId
                                schedule.operationId = operationId;
                            } catch (computeError) {
                                logger.error('❌ Failed to auto-compute operationId:', computeError);
                                statusUpdates[schedule.workflowId] = {
                                    isReady: false,
                                    isPending: false,
                                    isDone: false,
                                    error: 'Failed to compute operationId',
                                    lastChecked: Date.now()
                                };
                                continue;
                            }
                        }

                        if (operationId) {
                            try {
                                // TIMESTAMP-BASED CACHE BUSTING: Use 'latest' with fresh provider
                                // On forked chains, block number doesn't change, so we rely on fresh provider
                                const callOverrides = {
                                    blockTag: 'latest' as const
                                };

                                // BATCH: Check all three status functions with timestamp-based cache-busting
                                const [isReady, isPending, isDone] = await Promise.all([
                                    contract.isOperationReady(operationId, callOverrides),
                                    contract.isOperationPending(operationId, callOverrides),
                                    contract.isOperationDone(operationId, callOverrides)
                                ]);

                                statusUpdates[operationId] = {
                                    isReady,
                                    isPending,
                                    isDone,
                                    lastChecked: Date.now()
                                };

                                logger.log(`✅ Status for ${operationId.substring(0, 10)}...:`, {
                                    isReady,
                                    isPending,
                                    isDone
                                });

                            } catch (statusError) {
                                logger.error('❌ Failed to check status for operationId:', operationId, statusError);
                                statusUpdates[operationId] = {
                                    isReady: false,
                                    isPending: false,
                                    isDone: false,
                                    error: statusError instanceof Error ? statusError.message : String(statusError),
                                    lastChecked: Date.now()
                                };
                            }
                        }
                    }

                    setTimelockStatuses(statusUpdates);
                    logger.log('✅ Batch status check complete:', Object.keys(statusUpdates).length, 'operations checked');

                } catch (error) {
                    logger.error('❌ Batch timelock status check failed:', error);
                } finally {
                    setStatusLoading(false);
                }
            };

            // Load available workflow completions
            useEffect(() => {
                const loadSchedules = async () => {
                    try {
                        setLoading(true);
                        setError(null);

                        // Use workflowCompletionService instead of API call
                        const completionsData = await workflowCompletionService.listCompletedWorkflows();

                        if (completionsData && completionsData.length > 0) {
                            // FIXED: Filter for 'scheduled' workflows with proper tier1/tier2 structure support
                            const schedules = completionsData
                                .filter((completion: any) => {
                                    // Check if this is a timelock workflow with schedule data
                                    const hasTimelockName = completion.workflowName && completion.workflowName.includes('Timelock');
                                    const hasScheduleData = completion.scheduleData;

                                    // CRITICAL FIX: Check status in the right location (tier1 structure support)
                                    let isScheduled = false;
                                    if (completion.status === 'scheduled') {
                                        isScheduled = true; // Direct status
                                    } else if (completion.tier1?.status === 'scheduled') {
                                        isScheduled = true; // Tier1 status (actual database structure)
                                    } else if (completion.templateData?.status === 'scheduled') {
                                        isScheduled = true; // TemplateData status
                                    }

                                    const shouldInclude = hasTimelockName && hasScheduleData && isScheduled;

                                    logger.log('🔍 SCHEDULE FILTER DEBUG:', {
                                        workflowName: completion.workflowName,
                                        hasTimelockName,
                                        hasScheduleData,
                                        directStatus: completion.status,
                                        tier1Status: completion.tier1?.status,
                                        templateDataStatus: completion.templateData?.status,
                                        isScheduled,
                                        shouldInclude
                                    });

                                    return shouldInclude;
                                })
                                .map((completion: any) => ({
                                    ...completion,
                                    created: new Date(completion.executionTimestamp).toLocaleString(),
                                    formattedTxHash: completion.transactionHash ?
                                        `${completion.transactionHash.substring(0, 10)}...${completion.transactionHash.substring(completion.transactionHash.length - 8)}` :
                                        'N/A'
                                }))
                                .sort((a: any, b: any) => b.executionTimestamp - a.executionTimestamp);

                            setAvailableSchedules(schedules);
                            logger.log('✅ Loaded available schedules:', schedules.length);

                            // NEW: Check timelock statuses after loading schedules
                            if (schedules.length > 0) {
                                await checkTimelockStatuses(schedules);
                            }
                        } else {
                            // No error, just no data
                            setAvailableSchedules([]);
                            logger.log('ℹ️ No workflow completions found');
                        }
                    } catch (error) {
                        setError('Failed to load schedules from service');
                        logger.error('❌ Error loading schedules:', error);
                    } finally {
                        setLoading(false);
                    }
                };

                loadSchedules();
            }, []);

            // NEW: Auto-refresh timelock statuses every minute when modal is open
            useEffect(() => {
                if (availableSchedules.length === 0) return;

                const refreshInterval = setInterval(async () => {
                    logger.log('🔄 Auto-refreshing timelock statuses...');
                    await checkTimelockStatuses(availableSchedules);
                }, 60000); // 60 seconds

                return () => clearInterval(refreshInterval);
            }, [availableSchedules, timelockContract, signer]);

            // NEW: Enhanced status determination with real-time blockchain data
            const getEnhancedStatus = (schedule: any) => {
                const operationId = schedule.operationId;
                const timelockStatus = operationId ? timelockStatuses[operationId] : null;

                if (timelockStatus?.error) {
                    return { status: 'error', color: 'error', icon: '🔴', label: 'Error' };
                }

                if (timelockStatus?.isDone) {
                    return { status: 'completed', color: 'info', icon: '🔵', label: 'Completed' };
                }

                if (timelockStatus?.isReady) {
                    return { status: 'ready', color: 'success', icon: '🟢', label: 'Ready to Execute' };
                }

                if (timelockStatus?.isPending) {
                    return { status: 'pending', color: 'warning', icon: '🟡', label: 'Pending (Delay Active)' };
                }

                // Fallback to stored status if no real-time data
                const storedStatus = schedule.status?.toLowerCase();
                switch (storedStatus) {
                    case 'scheduled':
                        return { status: 'scheduled', color: 'secondary', icon: '⚪', label: 'Scheduled (Checking...)' };
                    default:
                        return { status: 'unknown', color: 'error', icon: '🔴', label: 'Unknown' };
                }
            };

            const handleSelectSchedule = (schedule: any) => {
                setSelectedSchedule(schedule);
                logger.log('📋 Schedule selected:', {
                    workflowName: schedule.workflowName,
                    transactionHash: schedule.transactionHash,
                    scheduleData: schedule.scheduleData
                });
            };

            const handleContinue = async () => {
                if (!selectedSchedule) {
                    alert('Please select a scheduled operation');
                    return;
                }

                const scheduleData = selectedSchedule.scheduleData;
                let finalSalt = scheduleData.salt;

                logger.log('✅ Completing schedule selection with data:', scheduleData);

                // CRITICAL FIX: If salt is empty or '0', try to extract it from the SCHEDULE function KV reference
                if (!finalSalt || finalSalt === '' || finalSalt === '0') {
                    logger.log('🔍 Salt is empty or zero, attempting to extract from schedule function KV reference...');
                    logger.log('🔍 Current salt value:', finalSalt);

                    // CRITICAL FIX: Look for the SCHEDULE function KV reference, not the Memer function reference
                    // The schedule data.data contains the Memer function reference
                    // We need to find the SCHEDULE function KV reference from the workflow completion
                    logger.log('🔍 Selected schedule completion data:', {
                        workflowId: selectedSchedule.workflowId,
                        transactionHash: selectedSchedule.transactionHash,
                        hasScheduleParams: !!selectedSchedule.scheduleParameters
                    });

                    try {
                        // ENHANCED: Try multiple sources for salt extraction
                        let extractedSalt = null;
                        let scheduleKvReference = null;

                        // Method 1: Look for schedule function KV reference in workflow completion data
                        // Check if there's a second KV reference that represents the schedule function
                        if (selectedSchedule.debugInfo?.kvReferences && selectedSchedule.debugInfo.kvReferences.length > 1) {
                            // The second KV reference should be the schedule function
                            scheduleKvReference = selectedSchedule.debugInfo.kvReferences[1];
                            logger.log('🔐 Method 1: Found schedule function KV reference from debug info:', scheduleKvReference);
                        }

                        // Method 2: Try to construct the schedule KV reference ID from the transaction hash
                        // Based on the pattern: fb_1755827968503_c5b4c961cc9aae09 (schedule) vs fb_1755827905976_cfbd2571ee891cb3 (memer)
                        if (!scheduleKvReference && selectedSchedule.transactionHash) {
                            // Look for a KV reference that was created around the same time as the transaction
                            // This is a heuristic approach - in a real system, we'd store this mapping explicitly
                            const txTimestamp = selectedSchedule.executionTimestamp;
                            const possibleScheduleRef = `fb_${txTimestamp}_${selectedSchedule.transactionHash.substring(2, 18)}`;

                            logger.log('🔍 Method 2: Attempting to construct schedule KV reference:', possibleScheduleRef);

                            // Try to retrieve this reference to see if it exists
                            try {
                                const testResult = await backendDataHandler.retrieveEncodedData(possibleScheduleRef, 'workflow');
                                if (testResult.success) {
                                    scheduleKvReference = possibleScheduleRef;
                                    logger.log('🎯 Method 2 SUCCESS: Found constructed schedule KV reference');
                                }
                            } catch (error) {
                                logger.log('⚠️ Method 2: Constructed reference not found, continuing...');
                            }
                        }

                        // Method 3: Extract salt from the schedule function KV reference if found
                        if (scheduleKvReference && backendDataHandler.isSecureReference(scheduleKvReference)) {
                            logger.log('🔐 Method 3: Retrieving schedule function data from KV reference...');

                            const retrievalResult = await backendDataHandler.autoDecryptIfNeeded(scheduleKvReference);

                            if (retrievalResult.success && retrievalResult.data) {
                                logger.log('✅ Successfully retrieved schedule function data');
                                logger.log('🔍 Retrieved data type:', typeof retrievalResult.data);
                                logger.log('🔍 Retrieved data preview:',
                                    typeof retrievalResult.data === 'string' ?
                                        retrievalResult.data.substring(0, 100) + '...' :
                                        'Non-string data'
                                );

                                // Extract salt from the schedule function hex data
                                extractedSalt = await extractSaltFromScheduleFunction(retrievalResult.data);

                                if (extractedSalt) {
                                    logger.log('🎯 Method 3 SUCCESS: Extracted salt from schedule function:', extractedSalt);
                                } else {
                                    logger.warn('⚠️ Method 3 FAILED: Could not extract salt from schedule function data');
                                }
                            } else {
                                logger.error('❌ Method 3 FAILED: Failed to retrieve schedule function data:', retrievalResult.error);
                            }
                        }

                        // Method 4: Try to get salt from sessionStorage (last generated salt)
                        if (!extractedSalt) {
                            logger.log('🔍 Method 4: Attempting to retrieve salt from sessionStorage...');
                            try {
                                const storedSalt = sessionStorage.getItem('lastGeneratedSalt');
                                if (storedSalt && storedSalt !== '0' && storedSalt !== '') {
                                    extractedSalt = storedSalt;
                                    logger.log('🎯 Method 4 SUCCESS: Retrieved salt from sessionStorage:', extractedSalt);
                                } else {
                                    logger.warn('⚠️ Method 4 FAILED: No valid salt found in sessionStorage');
                                }
                            } catch (error) {
                                logger.error('❌ Method 4 ERROR:', error);
                            }
                        }

                        // Method 5: Try to get salt from the completion data's schedule parameters
                        if (!extractedSalt && selectedSchedule.scheduleParameters?.salt) {
                            logger.log('🔍 Method 5: Attempting to retrieve salt from schedule parameters...');
                            const scheduleSalt = selectedSchedule.scheduleParameters.salt;
                            if (scheduleSalt && scheduleSalt !== '0' && scheduleSalt !== '') {
                                extractedSalt = scheduleSalt;
                                logger.log('🎯 Method 5 SUCCESS: Retrieved salt from schedule parameters:', extractedSalt);
                            } else {
                                logger.warn('⚠️ Method 5 FAILED: Schedule parameters salt is empty or zero');
                            }
                        }

                        // Apply the extracted salt if found
                        if (extractedSalt) {
                            finalSalt = extractedSalt;
                            logger.log('✅ SALT EXTRACTION SUCCESS: Using extracted salt:', finalSalt);
                        } else {
                            logger.error('❌ SALT EXTRACTION FAILED: All methods failed to extract salt');
                            logger.log('🔍 Available data for debugging:', {
                                originalSalt: scheduleData.salt,
                                memerKvReference: scheduleData.data,
                                scheduleKvReference: scheduleKvReference,
                                hasSessionStorage: !!sessionStorage.getItem('lastGeneratedSalt'),
                                hasScheduleParams: !!selectedSchedule.scheduleParameters?.salt,
                                debugKvRefs: selectedSchedule.debugInfo?.kvReferences
                            });
                        }

                    } catch (error) {
                        logger.error('❌ Error during salt extraction process:', error);
                    }
                } else {
                    logger.log('✅ Salt is already valid:', finalSalt);
                }

                // Complete the step with the final salt (either original or extracted)
                completeStep(step.id, {
                    target: scheduleData.target,
                    value: scheduleData.value,
                    data: scheduleData.data, // This is the KV reference
                    predecessor: scheduleData.predecessor,
                    salt: finalSalt, // Use extracted salt if original was empty
                    delay: scheduleData.delay,
                    workflowName: selectedSchedule.workflowName,
                    transactionHash: selectedSchedule.transactionHash,
                    completionId: selectedSchedule.id || selectedSchedule.completionId,
                    description: `Selected schedule from ${selectedSchedule.workflowName}${finalSalt !== scheduleData.salt ? ' (salt extracted from function data)' : ''}`
                });
                setShowStepDialog(false);
            };

            return (
                <Box sx={{ p: 2 }}>
                    <Alert severity="info" sx={{ mb: 2 }}>
                        <strong>Step 0: Select Scheduled Operation</strong>
                        <br />
                        Choose from available scheduled timelock operations that are ready for execution.
                    </Alert>

                    {loading && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                            <LinearProgress sx={{ width: '100%' }} />
                            <Typography variant="body2" sx={{ ml: 2 }}>
                                Loading available schedules...
                            </Typography>
                        </Box>
                    )}

                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}

                    {!loading && !error && availableSchedules.length === 0 && (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            No scheduled operations found. You need to complete a "Memer Function → Timelock → MultiSig" workflow first to create scheduled operations.
                        </Alert>
                    )}

                    {!loading && !error && availableSchedules.length > 0 && (
                        <Box>
                            <Typography variant="subtitle1" gutterBottom>
                                Available Scheduled Operations ({availableSchedules.length}):
                            </Typography>

                            <Box sx={{ maxHeight: 500, overflow: 'auto', mb: 2 }}>
                                {availableSchedules.map((schedule, index) => {
                                    // Determine status color based on schedule status
                                    const getStatusColor = (status: string) => {
                                        switch (status?.toLowerCase()) {
                                            case 'ready': return 'success'; // Green - can be executed
                                            case 'scheduled': return 'warning'; // Yellow - waiting for delay
                                            case 'completed': return 'info'; // Blue - already executed
                                            case 'pending': return 'secondary'; // Gray - pending
                                            default: return 'error'; // Red - unknown/failed
                                        }
                                    };

                                    const getStatusIcon = (status: string) => {
                                        switch (status?.toLowerCase()) {
                                            case 'ready': return '🟢';
                                            case 'scheduled': return '🟡';
                                            case 'completed': return '🔵';
                                            case 'pending': return '⚪';
                                            default: return '🔴';
                                        }
                                    };

                                    return (
                                        <Paper
                                            key={index}
                                            sx={{
                                                p: 2,
                                                mb: 2,
                                                cursor: 'pointer',
                                                border: selectedSchedule === schedule ? '3px solid' : '1px solid',
                                                borderColor: selectedSchedule === schedule ? 'primary.main' : 'divider',
                                                backgroundColor: selectedSchedule === schedule ? 'primary.light' : 'background.paper',
                                                '&:hover': {
                                                    boxShadow: 3,
                                                    backgroundColor: selectedSchedule === schedule ? 'primary.light' : 'grey.50'
                                                }
                                            }}
                                            onClick={() => handleSelectSchedule(schedule)}
                                        >
                                            {/* Header with Status */}
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Typography variant="subtitle2" fontWeight="bold">
                                                        {schedule.workflowName}
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        {getStatusIcon(schedule.status)}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                                    <Chip
                                                        label={schedule.status || 'unknown'}
                                                        color={getStatusColor(schedule.status)}
                                                        size="small"
                                                        variant="filled"
                                                    />
                                                    {selectedSchedule === schedule && (
                                                        <Chip
                                                            label="Selected"
                                                            color="primary"
                                                            size="small"
                                                            variant="outlined"
                                                        />
                                                    )}
                                                </Box>
                                            </Box>

                                            {/* Transaction Hash and Operation ID */}
                                            <Box sx={{ mb: 2 }}>
                                                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                                    <strong>Transaction Hash:</strong> {schedule.formattedTxHash}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                                    <strong>Operation ID:</strong> {schedule.operationId ?
                                                        `${schedule.operationId.substring(0, 20)}...${schedule.operationId.substring(schedule.operationId.length - 8)}` :
                                                        'Computing...'}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    <strong>Completed:</strong> {schedule.created}
                                                </Typography>
                                            </Box>

                                            {/* Comprehensive Schedule Parameters */}
                                            {schedule.scheduleData && (
                                                <Box sx={{
                                                    mt: 2,
                                                    p: 2,
                                                    bgcolor: selectedSchedule === schedule ? 'primary.dark' : 'grey.100',
                                                    borderRadius: 1,
                                                    color: selectedSchedule === schedule ? 'primary.contrastText' : 'text.primary'
                                                }}>
                                                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                                                        📋 Schedule Parameters
                                                    </Typography>

                                                    <Grid2 container spacing={1}>
                                                        <Grid2 size={{ xs: 12, sm: 6 }}>
                                                            <Typography variant="caption" sx={{ display: 'block' }}>
                                                                <strong>Target Contract:</strong>
                                                            </Typography>
                                                            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem', wordBreak: 'break-all' }}>
                                                                {schedule.scheduleData.target || 'MISSING'}
                                                            </Typography>
                                                        </Grid2>

                                                        <Grid2 size={{ xs: 12, sm: 6 }}>
                                                            <Typography variant="caption" sx={{ display: 'block' }}>
                                                                <strong>Value (ETH):</strong>
                                                            </Typography>
                                                            <Typography variant="body2">
                                                                {schedule.scheduleData.value || '0'} ETH
                                                            </Typography>
                                                        </Grid2>

                                                        <Grid2 size={{ xs: 12 }}>
                                                            <Typography variant="caption" sx={{ display: 'block' }}>
                                                                <strong>Data (KV Reference):</strong>
                                                            </Typography>
                                                            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                                                                {schedule.scheduleData.data ?
                                                                    (backendDataHandler.isSecureReference(schedule.scheduleData.data) ?
                                                                        backendDataHandler.formatReferenceForDisplay(schedule.scheduleData.data) :
                                                                        schedule.scheduleData.data.substring(0, 30) + '...'
                                                                    ) : 'MISSING'
                                                                }
                                                            </Typography>
                                                        </Grid2>

                                                        <Grid2 size={{ xs: 12, sm: 6 }}>
                                                            <Typography variant="caption" sx={{ display: 'block' }}>
                                                                <strong>Salt:</strong>
                                                            </Typography>
                                                            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem', wordBreak: 'break-all' }}>
                                                                {schedule.scheduleData.salt ?
                                                                    `${schedule.scheduleData.salt.substring(0, 20)}...${schedule.scheduleData.salt.substring(schedule.scheduleData.salt.length - 8)}` :
                                                                    'MISSING'
                                                                }
                                                            </Typography>
                                                        </Grid2>

                                                        <Grid2 size={{ xs: 12, sm: 6 }}>
                                                            <Typography variant="caption" sx={{ display: 'block' }}>
                                                                <strong>Delay:</strong>
                                                            </Typography>
                                                            <Typography variant="body2">
                                                                {schedule.scheduleData.delay ? `${schedule.scheduleData.delay} seconds` : 'MISSING'}
                                                            </Typography>
                                                        </Grid2>

                                                        <Grid2 size={{ xs: 12 }}>
                                                            <Typography variant="caption" sx={{ display: 'block' }}>
                                                                <strong>Predecessor:</strong>
                                                            </Typography>
                                                            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                                                                {schedule.scheduleData.predecessor || '0x0000...0000'}
                                                            </Typography>
                                                        </Grid2>
                                                    </Grid2>
                                                </Box>
                                            )}


                                            {/* Real-time Status Check */}
                                            <Box sx={{
                                                mt: 2, p: 1, bgcolor: getEnhancedStatus(schedule).color === 'success' ? 'success.light' :
                                                    getEnhancedStatus(schedule).color === 'warning' ? 'warning.light' :
                                                        getEnhancedStatus(schedule).color === 'info' ? 'info.light' :
                                                            getEnhancedStatus(schedule).color === 'error' ? 'error.light' : 'grey.100', borderRadius: 1
                                            }}>
                                                <Typography variant="caption" sx={{ display: 'block', fontWeight: 'bold', mb: 0.5 }}>
                                                    ⏱️ Timelock Status (Real-time):
                                                    {statusLoading && <span> 🔄 Refreshing...</span>}
                                                </Typography>
                                                {schedule.operationId ? (
                                                    <>
                                                        <Typography variant="caption" sx={{ display: 'block' }}>
                                                            • isOperationReady: {timelockStatuses[schedule.operationId]?.isReady ? '✅ YES' : '❌ NO'}
                                                        </Typography>
                                                        <Typography variant="caption" sx={{ display: 'block' }}>
                                                            • isOperationPending: {timelockStatuses[schedule.operationId]?.isPending ? '✅ YES' : '❌ NO'}
                                                        </Typography>
                                                        <Typography variant="caption" sx={{ display: 'block' }}>
                                                            • isOperationDone: {timelockStatuses[schedule.operationId]?.isDone ? '✅ YES' : '❌ NO'}
                                                        </Typography>
                                                        {timelockStatuses[schedule.operationId]?.error && (
                                                            <Typography variant="caption" sx={{ display: 'block', color: 'error.main' }}>
                                                                • Error: {timelockStatuses[schedule.operationId].error}
                                                            </Typography>
                                                        )}
                                                        {timelockStatuses[schedule.operationId]?.lastChecked && (
                                                            <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                                                                • Last checked: {new Date(timelockStatuses[schedule.operationId].lastChecked).toLocaleTimeString()}
                                                            </Typography>
                                                        )}
                                                    </>
                                                ) : (
                                                    <Typography variant="caption" sx={{ display: 'block', color: 'warning.main' }}>
                                                        ⚠️ Missing Operation ID - Computing from schedule parameters...
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Paper>
                                    );
                                })}
                            </Box>
                        </Box>
                    )}

                    {selectedSchedule && (
                        <Alert severity="success" sx={{ mb: 2 }}>
                            <strong>Selected Schedule:</strong>
                            <br />
                            <strong>Workflow:</strong> {selectedSchedule.workflowName}
                            <br />
                            <strong>Transaction:</strong> {selectedSchedule.formattedTxHash}
                            <br />
                            <strong>Target Contract:</strong> {selectedSchedule.scheduleData?.target?.substring(0, 20)}...
                            <br />
                            <br />
                            This schedule data will be used to populate the timelock.execute() parameters in the next step.
                        </Alert>
                    )}

                    <Box sx={{ mt: 2, display: 'flex', gap: 2, justifyContent: 'space-between' }}>
                        <Button
                            variant="outlined"
                            onClick={() => setShowStepDialog(false)}
                        >
                            Cancel Step
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleContinue}
                            disabled={!selectedSchedule}
                        >
                            Continue with Selected Schedule →
                        </Button>
                    </Box>
                </Box>
            );
        };

        setCurrentStepComponent(<ScheduleSelectorComponent />);
        setShowStepDialog(true);
    };

    // Execute timelock execute builder step (Step 1 of timelock_to_multisig workflow)
    const executeTimelockExecuteBuilderStep = async (step: WorkflowStep, inputs: { [key: string]: string }) => {
        logger.log('⚡ Executing timelock execute builder step:', step.id, inputs);

        // Get data from the schedule selection step
        const scheduleSelectionStep = stepStates['step0_schedule_selection'];
        const scheduleData = scheduleSelectionStep?.outputs;

        if (!scheduleData) {
            throw new Error('No schedule data available from previous step');
        }

        logger.log('📊 Schedule data for execute builder:', {
            target: scheduleData.target,
            value: scheduleData.value,
            data: scheduleData.data ? (backendDataHandler.isSecureReference(scheduleData.data) ?
                backendDataHandler.formatReferenceForDisplay(scheduleData.data) :
                scheduleData.data.substring(0, 20) + '...') : 'N/A',
            salt: scheduleData.salt?.substring(0, 20) + '...',
            predecessor: scheduleData.predecessor
        });

        // Auto-select timelock contract and execute function
        const setupTimelockExecuteStep = () => {
            try {
                // Find the timelock contract
                const timelockContract = contracts.find(c =>
                    c.name.toLowerCase().includes('timelock') ||
                    c.abi.some((item: any) => item.name === 'execute')
                );

                if (timelockContract) {
                    // Store the auto-selection data for TransactionBuilder
                    const autoSelectionData = {
                        contractAddress: timelockContract.address,
                        contractName: timelockContract.name,
                        functionName: 'execute',
                        workflowContext: true,
                        stepId: step.id,
                        // CRITICAL FIX: Include resolved inputs for TransactionBuilder auto-population
                        resolvedInputs: inputs
                    };

                    sessionStorage.setItem('workflowAutoSelection', JSON.stringify(autoSelectionData));
                    logger.log('✅ Auto-selection data stored for timelock execute step:', autoSelectionData);
                    logger.log('🔧 Including resolved inputs for auto-population:', inputs);
                } else {
                    logger.warn('⚠️ No timelock contract found!');
                }
            } catch (error) {
                logger.error('❌ Error setting up timelock execute auto-selection:', error);
            }
        };

        setupTimelockExecuteStep();

        // Create the step component with function built detection
        const TimelockExecuteBuilderComponent = () => {
            const [functionBuilt, setFunctionBuilt] = useState(false);
            const [executeKvReference, setExecuteKvReference] = useState<string>('');

            useEffect(() => {
                // Listen for functionBuilt events specifically for this step
                const handleFunctionBuilt = async (event: CustomEvent) => {
                    const { functionName, functionReference, secureReference } = event.detail;

                    logger.log('🎯 Step 1 received functionBuilt event:', {
                        functionName,
                        functionReference,
                        secureReference,
                        eventDetail: event.detail
                    });

                    // Check if this is the timelock.execute function being built
                    if (functionName === 'execute') {
                        logger.log('🎯 Timelock execute function built, capturing KV reference');

                        // Capture the KV reference from the timelock.execute function
                        const kvRef = secureReference || functionReference;
                        setExecuteKvReference(kvRef);
                        setFunctionBuilt(true);

                        logger.log('✅ Timelock execute KV reference captured:',
                            backendDataHandler.isSecureReference(kvRef) ?
                                backendDataHandler.formatReferenceForDisplay(kvRef) :
                                kvRef.substring(0, 30) + '...'
                        );

                        // UNIVERSAL PATTERN: PHASE 4.1 - Create workflow completion record immediately
                        logger.log('🎯 UNIVERSAL PHASE 4.1: Creating execute workflow completion record at Build Function time...');

                        try {
                            // Generate unique workflow ID for execute workflow
                            const executeWorkflowId = `execute_workflow_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

                            // Create execute workflow completion data with ACTUAL parameters from selected schedule
                            const executeCompletionData: WorkflowCompletionData = {
                                workflowId: executeWorkflowId,
                                workflowName: 'Timelock Execute → MultiSig',
                                transactionHash: '', // Will be filled in Phase 4.2
                                blockNumber: 0, // Will be filled in Phase 4.2
                                gasUsed: '0', // Will be filled in Phase 4.2
                                scheduleData: {
                                    target: scheduleData.target,
                                    value: scheduleData.value,
                                    data: scheduleData.data, // Original memer function KV reference
                                    predecessor: scheduleData.predecessor,
                                    salt: scheduleData.salt, // ACTUAL salt from selected schedule
                                    delay: scheduleData.delay // ACTUAL delay from selected schedule
                                },
                                executionTimestamp: Date.now(),
                                status: 'building_execute', // New status for execute workflow building
                                multiSigAddress: '', // Will be filled in Step 2a
                                functionName: 'execute',
                                originalFunctionData: kvRef, // Execute function KV reference
                                operationId: '', // FIXED: Add required operationId field
                                debugInfo: {
                                    saltCaptureMethod: 'selected_schedule_data',
                                    parameterSources: {
                                        target: 'selected_schedule',
                                        value: 'selected_schedule',
                                        data: 'selected_schedule_original_memer_function',
                                        predecessor: 'selected_schedule',
                                        salt: 'selected_schedule_actual_salt',
                                        delay: 'selected_schedule_actual_delay'
                                    },
                                    kvReferences: [
                                        scheduleData.data, // Original memer function KV ref
                                        kvRef // Execute function KV ref
                                    ].filter(Boolean),
                                    workflowSteps: ['step0_schedule_selection', 'step1_timelock_execute_building'],
                                    willUpdateAfterTransaction: true, // Will be updated in Phase 4.2
                                    originalScheduleOperationId: scheduleData.completionId || scheduleData.operationId
                                }
                            };

                            logger.log('💾 UNIVERSAL PHASE 4.1: Execute workflow completion data:', {
                                workflowId: executeCompletionData.workflowId,
                                saltValue: executeCompletionData.scheduleData.salt?.substring(0, 20) + '...',
                                delayValue: executeCompletionData.scheduleData.delay,
                                status: executeCompletionData.status,
                                originalScheduleId: executeCompletionData.debugInfo?.originalScheduleOperationId
                            });

                            // Store the early execute workflow completion record using ENHANCED method with tier1/tier2 structure
                            const storeResult = await workflowCompletionService.storeEnhancedWorkflowCompletion(executeCompletionData);

                            if (storeResult.success && storeResult.id) {
                                logger.log('✅ UNIVERSAL PHASE 4.1: Execute workflow completion record created successfully:', storeResult.id);

                                // Store the completion ID for later update in Step 2b
                                sessionStorage.setItem('earlyExecuteCompletionId', storeResult.id);
                                sessionStorage.setItem('earlyExecuteCompletionData', JSON.stringify(executeCompletionData));

                            } else {
                                logger.error('❌ UNIVERSAL PHASE 4.1: Failed to create execute workflow completion record:', storeResult.error || 'No ID returned');

                                // Store in sessionStorage as fallback
                                sessionStorage.setItem('earlyExecuteCompletionData', JSON.stringify(executeCompletionData));
                                logger.log('💾 UNIVERSAL PHASE 4.1: Stored execute completion data in sessionStorage as fallback');
                            }

                        } catch (error) {
                            logger.error('❌ UNIVERSAL PHASE 4.1: Error creating execute workflow completion record:', error);
                            // Don't fail the function building if workflow record creation fails
                        }
                    }
                };

                const eventListener = (event: Event) => {
                    handleFunctionBuilt(event as CustomEvent);
                };

                window.addEventListener('functionBuilt', eventListener);

                return () => {
                    window.removeEventListener('functionBuilt', eventListener);
                };
            }, []);

            const handleContinueToMultiSig = () => {
                logger.log('🎯 Continuing to MultiSig step with execute KV reference');

                // Complete this step with the execute function data
                completeStep(step.id, {
                    functionBuilt: true,
                    executeKvReference: executeKvReference,
                    scheduleData: scheduleData,
                    timestamp: Date.now(),
                    description: 'Timelock execute function built successfully'
                });

                setShowStepDialog(false);
            };

            return (
                <Box sx={{ p: 2 }}>
                    <Alert severity="info" sx={{ mb: 2 }}>
                        <strong>Step 1: Build Timelock Execute Call</strong>
                        <br />
                        Build the timelock.execute() function call with parameters from the selected schedule.
                    </Alert>

                    {/* Show data from schedule selection */}
                    <Alert severity="success" sx={{ mb: 2 }}>
                        <strong>From Selected Schedule:</strong>
                        <br />
                        <strong>Workflow:</strong> {scheduleData.workflowName}
                        <br />
                        <strong>Target Contract:</strong> {scheduleData.target}
                        <br />
                        <strong>Value:</strong> {scheduleData.value} ETH
                        <br />
                        <strong>Data (KV Ref):</strong> {scheduleData.data ? (backendDataHandler.isSecureReference(scheduleData.data) ?
                            backendDataHandler.formatReferenceForDisplay(scheduleData.data) :
                            scheduleData.data.substring(0, 30) + '...') : 'N/A'}
                        <br />
                        <strong>Salt:</strong> {scheduleData.salt?.substring(0, 30)}...
                        <br />
                        <strong>Predecessor:</strong> {scheduleData.predecessor}
                    </Alert>

                    <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 2, mb: 2 }}>
                        <TransactionBuilder workflowContext={workflowContext} />
                    </Box>

                    <Alert severity="info" sx={{ mb: 2 }}>
                        <strong>What's Auto-filled:</strong>
                        <br />
                        • <strong>Contract:</strong> Timelock Controller (auto-selected)
                        <br />
                        • <strong>Function:</strong> execute (auto-selected)
                        <br />
                        • <strong>Target:</strong> {scheduleData.target} (from schedule)
                        <br />
                        • <strong>Value:</strong> {scheduleData.value} (from schedule)
                        <br />
                        • <strong>Data:</strong> KV Reference (will be decrypted during build)
                        <br />
                        • <strong>Predecessor:</strong> {scheduleData.predecessor} (from schedule)
                        <br />
                        • <strong>Salt:</strong> {scheduleData.salt?.substring(0, 20)}... (from schedule)
                        <br />
                        <br />
                        <strong>Instructions:</strong>
                        <br />
                        1. Review the auto-filled parameters above
                        <br />
                        2. Click "Build Function" to generate the execute call
                        <br />
                        3. Click "Continue to MultiSig" when ready
                    </Alert>

                    {functionBuilt && (
                        <Alert severity="success" sx={{ mb: 2 }}>
                            <strong>✅ Execute Function Built!</strong>
                            <br />
                            The timelock.execute function has been successfully built. You can now continue to the MultiSig step.
                            <br />
                            <strong>KV Reference:</strong> {backendDataHandler.isSecureReference(executeKvReference) ?
                                backendDataHandler.formatReferenceForDisplay(executeKvReference) :
                                executeKvReference.substring(0, 30) + '...'}
                        </Alert>
                    )}

                    <Box sx={{ mt: 2, display: 'flex', gap: 2, justifyContent: 'space-between' }}>
                        <Button
                            variant="outlined"
                            onClick={() => setShowStepDialog(false)}
                        >
                            Cancel Step
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleContinueToMultiSig}
                            disabled={!functionBuilt}
                        >
                            {functionBuilt ? 'Continue to MultiSig →' : 'Build Execute Function First'}
                        </Button>
                    </Box>
                </Box>
            );
        };

        setCurrentStepComponent(<TimelockExecuteBuilderComponent />);
        setShowStepDialog(true);
    };

    // Execute Step 2a: Timelock MultiSig Selection (for timelock execute workflow)
    const executeTimelockMultiSigSelectionStep = async (step: WorkflowStep, inputs: { [key: string]: string }) => {
        logger.log('🔐 Executing Step 2a: Timelock MultiSig Selection', step.id, inputs);

        // Create a component that captures the selection without building
        const TimelockMultiSigSelectionComponent = () => {
            const [selectedContract, setSelectedContract] = useState<any>(null);
            const [selectedFunction, setSelectedFunction] = useState<string>('');
            const [selectionComplete, setSelectionComplete] = useState(false);

            // Find MultiSig contracts
            const multiSigContracts = contracts.filter(c =>
                c.name.toLowerCase().includes('multisig') ||
                c.abi.some((item: any) => item.name === 'execTransaction')
            );

            // Listen for contract and function selection from TransactionBuilder
            useEffect(() => {
                const handleContractSelection = () => {
                    // Check if both contract and function are selected
                    if (selectedContract && selectedFunction) {
                        setSelectionComplete(true);
                    }
                };

                handleContractSelection();
            }, [selectedContract, selectedFunction]);

            const handleContinue = () => {
                if (!selectedContract || !selectedFunction) {
                    alert('Please select both a MultiSig contract and function');
                    return;
                }

                logger.log('✅ Capturing Step 2a selection:', {
                    contract: selectedContract.name,
                    address: selectedContract.address,
                    function: selectedFunction
                });

                completeStep(step.id, {
                    multiSigAddress: selectedContract.address,
                    functionName: selectedFunction,
                    contractName: selectedContract.name,
                    description: `Selected ${selectedContract.name}.${selectedFunction}`
                });
                setShowStepDialog(false);
            };

            return (
                <Box sx={{ p: 2 }}>
                    <Alert severity="info" sx={{ mb: 2 }}>
                        <strong>Step 2a: Choose MultiSig & Function</strong>
                        <br />
                        Select your MultiSig wallet and execution function. Parameters will be auto-filled in the next step.
                    </Alert>

                    {/* Contract Selection */}
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle1" gutterBottom>
                            1. Select MultiSig Wallet:
                        </Typography>
                        <AddressSelector
                            value={selectedContract?.address || ''}
                            onChange={(address) => {
                                const contract = multiSigContracts.find(c => c.address === address);
                                setSelectedContract(contract || null);
                                logger.log('📋 Contract selected:', contract?.name);
                            }}
                            contracts={multiSigContracts}
                            label="MultiSig Wallet"
                            placeholder="Select your MultiSig wallet"
                            helperText="Choose the MultiSig wallet that will execute the timelock operation"
                        />
                    </Box>

                    {/* Function Selection */}
                    {selectedContract && (
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="subtitle1" gutterBottom>
                                2. Select Execution Function:
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <Button
                                    variant={selectedFunction === 'execTransaction' ? 'contained' : 'outlined'}
                                    onClick={() => {
                                        setSelectedFunction('execTransaction');
                                        logger.log('🔧 Function selected: execTransaction');
                                    }}
                                >
                                    execTransaction
                                </Button>
                                <Button
                                    variant={selectedFunction === 'executeTransactionWithChecks' ? 'contained' : 'outlined'}
                                    onClick={() => {
                                        setSelectedFunction('executeTransactionWithChecks');
                                        logger.log('🔧 Function selected: executeTransactionWithChecks');
                                    }}
                                >
                                    executeTransactionWithChecks
                                </Button>
                            </Box>
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                {selectedFunction === 'execTransaction'
                                    ? 'Standard execution without additional checks'
                                    : selectedFunction === 'executeTransactionWithChecks'
                                        ? 'Execution with additional safety checks'
                                        : 'Choose your preferred execution method'
                                }
                            </Typography>
                        </Box>
                    )}

                    {/* Selection Summary */}
                    {selectedContract && selectedFunction && (
                        <Alert severity="success" sx={{ mb: 2 }}>
                            <strong>Selection Summary:</strong>
                            <br />
                            <strong>MultiSig:</strong> {selectedContract.name}
                            <br />
                            <strong>Address:</strong> {selectedContract.address}
                            <br />
                            <strong>Function:</strong> {selectedFunction}
                            <br />
                            <br />
                            <strong>Next Step:</strong> Parameters will be auto-filled from the timelock execute step
                        </Alert>
                    )}

                    <Box sx={{ mt: 2, display: 'flex', gap: 2, justifyContent: 'space-between' }}>
                        <Button
                            variant="outlined"
                            onClick={() => setShowStepDialog(false)}
                        >
                            Cancel Step
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleContinue}
                            disabled={!selectedContract || !selectedFunction}
                        >
                            Continue to Build Transaction →
                        </Button>
                    </Box>
                </Box>
            );
        };

        setCurrentStepComponent(<TimelockMultiSigSelectionComponent />);
        setShowStepDialog(true);
    };

    // Execute Step 2b: Timelock MultiSig Build (for timelock execute workflow)
    const executeTimelockMultiSigBuildStep = async (step: WorkflowStep, inputs: { [key: string]: string }) => {
        logger.log('🏗️ Executing Step 2b: Timelock MultiSig Build', step.id, inputs);

        // Get data from previous steps
        const step2aData = stepStates['step2a_timelock_multisig_selection'];
        const step1Data = stepStates['step1_timelock_execute_builder'];

        const selectedMultiSig = step2aData?.outputs?.multiSigAddress || '';
        const selectedFunction = step2aData?.outputs?.functionName || 'execTransaction';

        // Get timelock controller address from contracts
        const timelockContract = contracts.find(c =>
            c.name.toLowerCase().includes('timelock') ||
            c.abi.some((item: any) => item.name === 'execute')
        );
        const timelockController = timelockContract?.address || '';

        // Use the timelock.execute KV reference from Step 1
        const executeKvReference = step1Data?.outputs?.executeKvReference || '0x';
        const isKvReference = executeKvReference && backendDataHandler.isSecureReference(executeKvReference);

        logger.log('📊 Step 2b data sources:', {
            step2aData: step2aData?.outputs,
            step1Data: step1Data?.outputs,
            selectedMultiSig,
            selectedFunction,
            timelockController,
            executeKvReference: isKvReference ? backendDataHandler.formatReferenceForDisplay(executeKvReference) : executeKvReference.substring(0, 30) + '...',
            isKvReference,
            dataSource: 'Using timelock.execute KV reference from Step 1'
        });

        // Create the Step 2b component
        const TimelockMultiSigBuildComponent = () => {
            const [autoSelectionSet, setAutoSelectionSet] = useState(false);

            useEffect(() => {
                // Clear any existing auto-selection data first
                sessionStorage.removeItem('workflowAutoSelection');

                // Small delay to ensure clean state
                setTimeout(() => {
                    // Setup auto-selection for TransactionBuilder
                    const autoSelectionData = {
                        contractAddress: selectedMultiSig,
                        contractName: 'MultiSig Wallet',
                        functionName: selectedFunction,
                        workflowContext: true,
                        stepId: step.id,
                        prefilledInputs: {
                            to: timelockController,
                            value: '0',
                            data: executeKvReference, // Use timelock.execute KV reference
                            operation: '0',
                            safeTxGas: '0',
                            baseGas: '0',
                            gasPrice: '0',
                            gasToken: '0x0000000000000000000000000000000000000000',
                            refundReceiver: selectedMultiSig,
                            signatures: '0x'
                        }
                    };

                    sessionStorage.setItem('workflowAutoSelection', JSON.stringify(autoSelectionData));
                    logger.log('✅ Auto-selection data stored for Step 2b:', autoSelectionData);

                    // Force a small delay before showing TransactionBuilder to ensure it picks up the data
                    setTimeout(() => {
                        setAutoSelectionSet(true);
                    }, 100);
                }, 50);
            }, []);

            return (
                <Box sx={{ p: 2 }}>
                    <Alert severity="info" sx={{ mb: 2 }}>
                        <strong>Step 2b: Build MultiSig Transaction</strong>
                        <br />
                        Build the MultiSig transaction with auto-filled parameters from previous steps. All values are pre-populated but editable.
                    </Alert>

                    {/* Show selection from Step 2a */}
                    <Alert severity="success" sx={{ mb: 2 }}>
                        <strong>From Step 2a:</strong>
                        <br />
                        <strong>MultiSig:</strong> {step2aData?.outputs?.contractName || 'MultiSig Wallet'}
                        <br />
                        <strong>Address:</strong> {selectedMultiSig}
                        <br />
                        <strong>Function:</strong> {selectedFunction}
                    </Alert>

                    {/* FunctionExplorer with auto-selection and transaction completion callback */}
                    {autoSelectionSet && (
                        <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 2, mb: 2 }}>
                            <FunctionExplorer
                                key={`fe-${selectedMultiSig}-${selectedFunction}-${Date.now()}`}
                                workflowContext={workflowContext}
                                onTransactionComplete={async (transactionHash: string, receipt: any, executionContext: any) => {
                                    logger.log('🎯 Timelock Execute Workflow: Transaction completed in FunctionExplorer!', {
                                        transactionHash,
                                        blockNumber: receipt.blockNumber,
                                        gasUsed: receipt.gasUsed.toString()
                                    });

                                    try {
                                        // PHASE 4.2: UPDATE EXISTING WORKFLOW RECORD with execution details
                                        logger.log('🎯 PHASE 4.2: Updating existing execute workflow completion record with execution details...');

                                        // Check if we have an early execute workflow completion ID from Phase 4.1
                                        const earlyExecuteCompletionId = sessionStorage.getItem('earlyExecuteCompletionId');
                                        const earlyExecuteCompletionData = sessionStorage.getItem('earlyExecuteCompletionData');

                                        if (earlyExecuteCompletionId && earlyExecuteCompletionData) {
                                            logger.log('✅ PHASE 4.2: Found early execute workflow completion record to update:', earlyExecuteCompletionId);

                                            try {
                                                const parsedEarlyData = JSON.parse(earlyExecuteCompletionData);

                                                // Create updated completion data with execution details
                                                const updatedExecuteCompletionData: WorkflowCompletionData = {
                                                    ...parsedEarlyData,
                                                    transactionHash, // Add actual transaction hash
                                                    blockNumber: receipt.blockNumber, // Add block number
                                                    gasUsed: receipt.gasUsed.toString(), // Add gas used
                                                    status: 'completed', // Update status from 'building_execute' to 'completed'
                                                    multiSigAddress: selectedMultiSig, // Add MultiSig address
                                                    executionTimestamp: Date.now(), // Update execution timestamp
                                                    debugInfo: {
                                                        ...parsedEarlyData.debugInfo,
                                                        workflowSteps: [...(parsedEarlyData.debugInfo?.workflowSteps || []), 'step2a_timelock_multisig_selection', 'step2b_timelock_multisig_build_completed'],
                                                        executionDetails: {
                                                            transactionHash,
                                                            blockNumber: receipt.blockNumber,
                                                            gasUsed: receipt.gasUsed.toString(),
                                                            multiSigAddress: selectedMultiSig,
                                                            functionName: selectedFunction,
                                                            completedAt: Date.now(),
                                                            updateMethod: 'phase_4_2_execute_update'
                                                        }
                                                    }
                                                };

                                                logger.log('💾 PHASE 4.2: Updating execute workflow completion record with execution details:', {
                                                    id: earlyExecuteCompletionId,
                                                    transactionHash,
                                                    blockNumber: receipt.blockNumber,
                                                    gasUsed: receipt.gasUsed.toString(),
                                                    status: 'completed'
                                                });

                                                // Update the existing record
                                                const updateResult = await workflowCompletionService.updateWorkflowCompletion(earlyExecuteCompletionId, updatedExecuteCompletionData);

                                                if (updateResult.success) {
                                                    logger.log('✅ PHASE 4.2: Successfully updated existing execute workflow completion record');

                                                    // Clean up sessionStorage
                                                    sessionStorage.removeItem('earlyExecuteCompletionId');
                                                    sessionStorage.removeItem('earlyExecuteCompletionData');
                                                    logger.log('🧹 PHASE 4.2: Cleaned up early execute completion sessionStorage data');
                                                } else {
                                                    logger.error('❌ PHASE 4.2: Failed to update existing execute workflow completion record:', updateResult.error);
                                                    throw new Error(`Failed to update execute workflow completion: ${updateResult.error}`);
                                                }

                                            } catch (parseError) {
                                                logger.error('❌ PHASE 4.2: Error parsing early execute completion data:', parseError);
                                                throw parseError;
                                            }
                                        } else {
                                            logger.log('ℹ️ PHASE 4.2: No early execute workflow completion record found, skipping update...');
                                        }

                                        // Complete the workflow step with comprehensive data
                                        completeStep(step.id, {
                                            executionTxHash: transactionHash,
                                            blockNumber: receipt.blockNumber,
                                            gasUsed: receipt.gasUsed.toString(),
                                            multiSigAddress: selectedMultiSig,
                                            functionName: selectedFunction,
                                            timelockController: timelockController,
                                            executeKvReference: executeKvReference,
                                            scheduleData: step1Data?.outputs?.scheduleData,
                                            description: `✅ Timelock Execute Workflow completed! Transaction: ${transactionHash}`
                                        });

                                        // Close the dialog
                                        setShowStepDialog(false);

                                    } catch (error) {
                                        logger.error('❌ Error handling transaction completion:', error);
                                        // Still complete the step even if storage fails
                                        completeStep(step.id, {
                                            executionTxHash: transactionHash,
                                            blockNumber: receipt.blockNumber,
                                            gasUsed: receipt.gasUsed.toString(),
                                            multiSigAddress: selectedMultiSig,
                                            functionName: selectedFunction,
                                            error: `Transaction completed but processing failed: ${error instanceof Error ? error.message : String(error)}`,
                                            description: `⚠️ Transaction completed with processing error: ${transactionHash}`
                                        });
                                        setShowStepDialog(false);
                                    }
                                }}
                            />
                        </Box>
                    )}

                    {!autoSelectionSet && (
                        <Box sx={{ p: 2, textAlign: 'center' }}>
                            <Typography>Setting up auto-selection...</Typography>
                        </Box>
                    )}

                    <Alert severity="info" sx={{ mb: 2 }}>
                        <strong>What's Pre-filled:</strong>
                        <br />
                        • <strong>Contract:</strong> {selectedMultiSig} (from Step 2a)
                        <br />
                        • <strong>Function:</strong> {selectedFunction} (from Step 2a)
                        <br />
                        • <strong>To:</strong> {timelockController} (Timelock Controller)
                        <br />
                        • <strong>Data:</strong> {executeKvReference.substring(0, 30)}... (Timelock Execute KV Reference)
                        <br />
                        • <strong>RefundReceiver:</strong> {selectedMultiSig} (selected MultiSig)
                        <br />
                        • <strong>All other parameters:</strong> Set to defaults (0, 0x0000...)
                        <br />
                        <br />
                        <strong>Instructions:</strong>
                        <br />
                        1. Review the pre-filled parameters above
                        <br />
                        2. Edit any values if needed in the form fields
                        <br />
                        3. Click "Build Function" to generate the transaction
                        <br />
                        4. Click "Capture Transaction & Continue" when ready
                    </Alert>

                    <Box sx={{ mt: 2, display: 'flex', gap: 2, justifyContent: 'space-between' }}>
                        <Button
                            variant="outlined"
                            onClick={() => setShowStepDialog(false)}
                        >
                            Cancel Step
                        </Button>
                        <Alert severity="info" sx={{ flexGrow: 1, ml: 2 }}>
                            <strong>Automatic Execution:</strong> The transaction will be executed automatically when you complete the signature collection in the FunctionExplorer above. No additional button needed!
                        </Alert>
                    </Box>
                </Box>
            );
        };

        setCurrentStepComponent(<TimelockMultiSigBuildComponent />);
        setShowStepDialog(true);
    };

    // Execute timelock execute step (legacy - kept for compatibility)
    const executeTimelockExecuteStep = async (step: WorkflowStep, inputs: { [key: string]: string }) => {
        logger.log('Executing timelock execute step:', step.id, inputs);

        // Similar to timelock schedule but for execution
        setTimeout(() => {
            completeStep(step.id, {
                executionTxHash: '0x' + Math.random().toString(16).substr(2, 64)
            });
        }, 2000);
    };

    // Handle user input change
    const handleUserInputChange = (stepId: string, paramName: string, value: string) => {
        setUserInputs(prev => ({
            ...prev,
            [stepId]: {
                ...prev[stepId],
                [paramName]: value
            }
        }));
    };

    // Render step status icon
    const renderStepIcon = (stepId: string) => {
        const state = stepStates[stepId];
        switch (state.status) {
            case 'completed':
                return <CompleteIcon color="success" />;
            case 'error':
                return <ErrorIcon color="error" />;
            case 'active':
                return <StartIcon color="primary" />;
            default:
                return <TimeIcon color="disabled" />;
        }
    };

    // Render step inputs
    const renderStepInputs = (step: WorkflowStep) => {
        const inputMappings = Object.entries(step.inputMappings).filter(
            ([_, mapping]) => typeof mapping === 'object' && mapping.type === 'input'
        );

        if (inputMappings.length === 0) {
            return null;
        }

        // Check if this is a timelock schedule step that needs salt and delay utilities
        const isTimelockStep = step.type === 'timelock_schedule';
        const hasSaltParam = inputMappings.some(([paramName]) => paramName.toLowerCase() === 'salt');
        const hasDelayParam = inputMappings.some(([paramName]) => paramName.toLowerCase() === 'delay');
        const showUtilities = isTimelockStep && (hasSaltParam || hasDelayParam);

        // Find timelock contract for utilities
        const timelockContract = contracts.find(c => c.name.toLowerCase().includes('timelock'));

        return (
            <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 2 }}>
                    Required Inputs:
                </Typography>
                <Grid2 container spacing={2}>
                    {inputMappings.map(([paramName, _]) => (
                        <Grid2 size={{ xs: 12, sm: 6 }} key={paramName}>
                            <TextField
                                label={paramName}
                                value={userInputs[step.id]?.[paramName] || ''}
                                onChange={(e) => handleUserInputChange(step.id, paramName, e.target.value)}
                                fullWidth
                                size="small"
                                placeholder={
                                    paramName.toLowerCase() === 'salt' ? 'Generate using utility below' :
                                        paramName.toLowerCase() === 'delay' ? 'Fetch minimum delay below' :
                                            undefined
                                }
                            />
                        </Grid2>
                    ))}
                </Grid2>

                {/* Utility Tools for Timelock Steps */}
                {showUtilities && (
                    <Box sx={{ mt: 3, p: 2, bgcolor: 'primary.light', borderRadius: 1 }}>
                        <Typography variant="subtitle2" sx={{ mb: 2, color: 'primary.contrastText' }}>
                            🛠️ Utility Tools
                        </Typography>
                        <Grid2 container spacing={2}>
                            {hasSaltParam && (
                                <Grid2 size={{ xs: 12, sm: 6 }}>
                                    <Paper sx={{ p: 2, height: '100%' }}>
                                        <SaltGenerator
                                            contractAddress={timelockContract?.address}
                                            functionName="schedule"
                                            onSaltGenerated={(salt, id) => {
                                                handleUserInputChange(step.id, 'salt', salt);
                                                logger.log("Salt updated from generator:", salt);
                                            }}
                                        />
                                    </Paper>
                                </Grid2>
                            )}
                            {hasDelayParam && (
                                <Grid2 size={{ xs: 12, sm: 6 }}>
                                    <Paper sx={{ p: 2, height: '100%' }}>
                                        <DelayHelper
                                            contractAddress={timelockContract?.address}
                                            onDelayFetched={(delay) => {
                                                handleUserInputChange(step.id, 'delay', delay);
                                                logger.log("Delay updated from helper:", delay);
                                            }}
                                        />
                                    </Paper>
                                </Grid2>
                            )}
                        </Grid2>
                    </Box>
                )}
            </Box>
        );
    };

    if (!isConnected) {
        return (
            <Alert severity="warning">
                Please connect your wallet to execute workflows.
            </Alert>
        );
    }

    return (
        <Box>
            {/* Workflow Header */}
            <Paper sx={{ p: 3, mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box>
                        <Typography variant="h5" gutterBottom>
                            {template.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {template.description}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Chip
                            label={`${completedSteps}/${template.steps.length} steps`}
                            color="primary"
                            variant="outlined"
                        />
                        <Chip
                            label={`~${Math.round(remainingTime / 60)} min remaining`}
                            color="secondary"
                            variant="outlined"
                        />
                    </Box>
                </Box>

                {/* Progress Bar */}
                <Box sx={{ mb: 2 }}>
                    <LinearProgress
                        variant="determinate"
                        value={progress}
                        sx={{ height: 8, borderRadius: 4 }}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        {progress}% complete
                    </Typography>
                </Box>

                {/* Action Buttons */}
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                        variant="outlined"
                        onClick={onCancel}
                        startIcon={<BackIcon />}
                    >
                        Cancel Workflow
                    </Button>
                    {currentStepState?.status === 'error' && (
                        <Button
                            variant="contained"
                            onClick={() => executeStep(currentStep)}
                            startIcon={<RetryIcon />}
                            color="warning"
                        >
                            Retry Step
                        </Button>
                    )}
                </Box>
            </Paper>

            {/* Workflow Steps */}
            <Paper sx={{ p: 3 }}>
                <Stepper activeStep={activeStep} orientation="vertical">
                    {template.steps.map((step, index) => {
                        const stepState = stepStates[step.id];
                        const isActive = index === activeStep;

                        return (
                            <Step key={step.id} completed={stepState.status === 'completed'}>
                                <StepLabel
                                    icon={renderStepIcon(step.id)}
                                    error={stepState.status === 'error'}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography variant="subtitle1">
                                            {step.title}
                                        </Typography>
                                        {stepState.status === 'active' && (
                                            <Chip label="In Progress" size="small" color="primary" />
                                        )}
                                        {stepState.status === 'error' && (
                                            <Chip label="Error" size="small" color="error" />
                                        )}
                                    </Box>
                                </StepLabel>
                                <StepContent>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                        {step.description}
                                    </Typography>

                                    {/* Step Error */}
                                    {stepState.status === 'error' && stepState.error && (
                                        <Alert severity="error" sx={{ mb: 2 }}>
                                            {stepState.error}
                                        </Alert>
                                    )}

                                    {/* Step Inputs */}
                                    {isActive && renderStepInputs(step)}

                                    {/* Step Actions */}
                                    {isActive && stepState.status !== 'completed' && (
                                        <Box sx={{ mt: 2 }}>
                                            <Button
                                                variant="contained"
                                                onClick={() => {
                                                    logger.log('🔘 Execute Step button clicked for:', step.id);
                                                    executeStep(step);
                                                }}
                                                startIcon={<NextIcon />}
                                                disabled={stepState.status === 'active' && showStepDialog}
                                            >
                                                {stepState.status === 'active' && showStepDialog ? 'Dialog Open...' :
                                                    stepState.status === 'active' ? 'Execute Step' : 'Execute Step'}
                                            </Button>
                                            {stepState.status === 'active' && !showStepDialog && (
                                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                                    Click "Execute Step" to open the Transaction Builder dialog
                                                </Typography>
                                            )}
                                        </Box>
                                    )}

                                    {/* Step Results */}
                                    {stepState.outputs && (
                                        <Box sx={{ mt: 2, p: 2, bgcolor: 'success.light', borderRadius: 1 }}>
                                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                                Step Results:
                                            </Typography>
                                            <pre style={{ fontSize: '0.8rem', margin: 0 }}>
                                                {JSON.stringify(stepState.outputs, null, 2)}
                                            </pre>
                                        </Box>
                                    )}
                                </StepContent>
                            </Step>
                        );
                    })}
                </Stepper>
            </Paper>

            {/* Step Execution Dialog */}
            <Dialog
                open={showStepDialog}
                onClose={() => setShowStepDialog(false)}
                maxWidth="lg"
                fullWidth
            >
                <DialogTitle>
                    Execute Step: {currentStep?.title}
                </DialogTitle>
                <DialogContent>
                    {currentStepComponent}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowStepDialog(false)}>
                        Cancel
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Enhanced Success Modal with Transaction Hash Display */}
            <Dialog
                open={showSuccessModal}
                onClose={() => { }} // Disable backdrop/escape close
                maxWidth="md"
                fullWidth
                disableEscapeKeyDown
            >
                <DialogTitle sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    bgcolor: 'success.main',
                    color: 'success.contrastText'
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CompleteIcon />
                        <Typography variant="h6">
                            🎉 Workflow Completed Successfully!
                        </Typography>
                    </Box>
                    <IconButton
                        onClick={() => {
                            setShowSuccessModal(false);
                            onComplete(workflowResults || {});
                        }}
                        sx={{ color: 'success.contrastText' }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ p: 3 }}>
                    {workflowResults && (
                        <Box>
                            {/* ENHANCED: Transaction Hash Display with Copy Button */}
                            {(workflowResults.step3b_multisig_build?.executionTxHash ||
                                workflowResults.step4_signature_execution?.executionTxHash ||
                                workflowResults[Object.keys(workflowResults)[Object.keys(workflowResults).length - 1]]?.executionTxHash) && (
                                    <Paper sx={{
                                        p: 3,
                                        mb: 3,
                                        bgcolor: 'success.light',
                                        border: '2px solid',
                                        borderColor: 'success.main'
                                    }}>
                                        <Typography variant="h6" sx={{ mb: 2, color: 'success.dark', display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <SecurityIcon />
                                            📋 Transaction Hash
                                        </Typography>
                                        <Box sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            p: 2,
                                            bgcolor: 'white',
                                            borderRadius: 1,
                                            border: '1px solid',
                                            borderColor: 'success.main'
                                        }}>
                                            <Chip
                                                label="TX"
                                                color="success"
                                                size="small"
                                                sx={{ mr: 1 }}
                                            />
                                            <Typography
                                                variant="body1"
                                                sx={{
                                                    fontFamily: 'monospace',
                                                    fontSize: '1.1rem',
                                                    fontWeight: 'bold',
                                                    color: 'success.dark',
                                                    flexGrow: 1,
                                                    wordBreak: 'break-all'
                                                }}
                                            >
                                                {workflowResults.step3b_multisig_build?.executionTxHash ||
                                                    workflowResults.step4_signature_execution?.executionTxHash ||
                                                    workflowResults[Object.keys(workflowResults)[Object.keys(workflowResults).length - 1]]?.executionTxHash}
                                            </Typography>
                                            <Tooltip title="Copy Transaction Hash">
                                                <IconButton
                                                    onClick={() => {
                                                        const txHash = workflowResults.step3b_multisig_build?.executionTxHash ||
                                                            workflowResults.step4_signature_execution?.executionTxHash ||
                                                            workflowResults[Object.keys(workflowResults)[Object.keys(workflowResults).length - 1]]?.executionTxHash;
                                                        if (txHash) {
                                                            navigator.clipboard.writeText(txHash);
                                                            logger.log('✅ Transaction hash copied to clipboard:', txHash);

                                                            // Show visual feedback
                                                            const button = document.activeElement as HTMLElement;
                                                            if (button) {
                                                                button.style.backgroundColor = '#4caf50';
                                                                setTimeout(() => {
                                                                    button.style.backgroundColor = '';
                                                                }, 200);
                                                            }
                                                        }
                                                    }}
                                                    color="success"
                                                    size="small"
                                                >
                                                    <CopyIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>

                                        {/* ENHANCED: Additional Transaction Details with Chips */}
                                        <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                            {(workflowResults.step3b_multisig_build?.blockNumber ||
                                                workflowResults[Object.keys(workflowResults)[Object.keys(workflowResults).length - 1]]?.blockNumber) && (
                                                    <Chip
                                                        label={`Block: ${workflowResults.step3b_multisig_build?.blockNumber ||
                                                            workflowResults[Object.keys(workflowResults)[Object.keys(workflowResults).length - 1]]?.blockNumber}`}
                                                        color="success"
                                                        variant="outlined"
                                                        size="small"
                                                        icon={<CompleteIcon />}
                                                    />
                                                )}
                                            {(workflowResults.step3b_multisig_build?.gasUsed ||
                                                workflowResults[Object.keys(workflowResults)[Object.keys(workflowResults).length - 1]]?.gasUsed) && (
                                                    <Chip
                                                        label={`Gas: ${workflowResults.step3b_multisig_build?.gasUsed ||
                                                            workflowResults[Object.keys(workflowResults)[Object.keys(workflowResults).length - 1]]?.gasUsed}`}
                                                        color="success"
                                                        variant="outlined"
                                                        size="small"
                                                        icon={<SecurityIcon />}
                                                    />
                                                )}
                                            <Chip
                                                label="Workflow Complete"
                                                color="success"
                                                variant="filled"
                                                size="small"
                                                icon={<CompleteIcon />}
                                            />
                                            {(workflowResults.step3b_multisig_build?.completionId ||
                                                workflowResults[Object.keys(workflowResults)[Object.keys(workflowResults).length - 1]]?.completionId) && (
                                                    <Chip
                                                        label="Data Stored"
                                                        color="primary"
                                                        variant="outlined"
                                                        size="small"
                                                        icon={<SecurityIcon />}
                                                    />
                                                )}
                                        </Box>
                                    </Paper>
                                )}

                            {/* ENHANCED: Workflow Summary with Better Layout */}
                            <Paper sx={{ p: 3, mb: 3 }}>
                                <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <CompleteIcon color="success" />
                                    📊 Workflow Summary
                                </Typography>
                                <List dense>
                                    <ListItem>
                                        <ListItemIcon>
                                            <Chip label="Name" color="primary" size="small" />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={template.name}
                                            secondary="Workflow completed successfully"
                                        />
                                    </ListItem>
                                    {(workflowResults.step3b_multisig_build?.blockNumber ||
                                        workflowResults[Object.keys(workflowResults)[Object.keys(workflowResults).length - 1]]?.blockNumber) && (
                                            <ListItem>
                                                <ListItemIcon>
                                                    <Chip label="Block" color="success" size="small" />
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={workflowResults.step3b_multisig_build?.blockNumber ||
                                                        workflowResults[Object.keys(workflowResults)[Object.keys(workflowResults).length - 1]]?.blockNumber}
                                                    secondary="Block number where transaction was mined"
                                                />
                                            </ListItem>
                                        )}
                                    {(workflowResults.step3b_multisig_build?.gasUsed ||
                                        workflowResults[Object.keys(workflowResults)[Object.keys(workflowResults).length - 1]]?.gasUsed) && (
                                            <ListItem>
                                                <ListItemIcon>
                                                    <Chip label="Gas" color="warning" size="small" />
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={workflowResults.step3b_multisig_build?.gasUsed ||
                                                        workflowResults[Object.keys(workflowResults)[Object.keys(workflowResults).length - 1]]?.gasUsed}
                                                    secondary="Total gas consumed by transaction"
                                                />
                                            </ListItem>
                                        )}
                                    {(workflowResults.step3b_multisig_build?.completionId ||
                                        workflowResults[Object.keys(workflowResults)[Object.keys(workflowResults).length - 1]]?.completionId) && (
                                            <ListItem>
                                                <ListItemIcon>
                                                    <Chip label="ID" color="secondary" size="small" />
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={`${(workflowResults.step3b_multisig_build?.completionId ||
                                                        workflowResults[Object.keys(workflowResults)[Object.keys(workflowResults).length - 1]]?.completionId)?.substring(0, 20)}...`}
                                                    secondary="Completion record stored in database"
                                                />
                                            </ListItem>
                                        )}
                                </List>
                            </Paper>

                            {/* ENHANCED: Quick Actions with Better Icons */}
                            <Paper sx={{ p: 3 }}>
                                <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <LaunchIcon color="primary" />
                                    🔗 Quick Actions
                                </Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    {(workflowResults.step3b_multisig_build?.executionTxHash ||
                                        workflowResults[Object.keys(workflowResults)[Object.keys(workflowResults).length - 1]]?.executionTxHash) && (
                                            <>
                                                <Button
                                                    variant="outlined"
                                                    startIcon={<TimeIcon />}
                                                    onClick={() => {
                                                        const txHash = workflowResults.step3b_multisig_build?.executionTxHash ||
                                                            workflowResults[Object.keys(workflowResults)[Object.keys(workflowResults).length - 1]]?.executionTxHash;
                                                        logger.log('🔗 Navigating to Timelock Operations with TX:', txHash);

                                                        // Emit navigation event
                                                        const navigationEvent = new CustomEvent('navigateToTab', {
                                                            detail: {
                                                                tab: 'timelock-operations',
                                                                action: 'highlight-transaction',
                                                                transactionHash: txHash,
                                                                workflowId: workflowKey,
                                                                workflowName: template.name
                                                            }
                                                        });
                                                        window.dispatchEvent(navigationEvent);
                                                    }}
                                                    fullWidth
                                                    sx={{ justifyContent: 'flex-start' }}
                                                >
                                                    View in Timelock Operations
                                                </Button>
                                                <Button
                                                    variant="outlined"
                                                    startIcon={<LaunchIcon />}
                                                    onClick={() => {
                                                        const txHash = workflowResults.step3b_multisig_build?.executionTxHash ||
                                                            workflowResults[Object.keys(workflowResults)[Object.keys(workflowResults).length - 1]]?.executionTxHash;
                                                        logger.log('🔗 Navigating to Transaction History with TX:', txHash);
                                                    }}
                                                    fullWidth
                                                    sx={{ justifyContent: 'flex-start' }}
                                                >
                                                    View in Transaction History
                                                </Button>
                                            </>
                                        )}
                                    <Button
                                        variant="outlined"
                                        startIcon={<SecurityIcon />}
                                        onClick={() => {
                                            const txHash = workflowResults.step3b_multisig_build?.executionTxHash ||
                                                workflowResults[Object.keys(workflowResults)[Object.keys(workflowResults).length - 1]]?.executionTxHash;
                                            logger.log('🔗 Navigating to Database Maintenance with completion:', txHash);

                                            // Emit navigation event
                                            const navigationEvent = new CustomEvent('navigateToTab', {
                                                detail: {
                                                    tab: 'database-maintenance',
                                                    action: 'show-workflow-completions',
                                                    transactionHash: txHash,
                                                    workflowId: workflowKey,
                                                    workflowName: template.name
                                                }
                                            });
                                            window.dispatchEvent(navigationEvent);
                                        }}
                                        fullWidth
                                        sx={{ justifyContent: 'flex-start' }}
                                    >
                                        View Workflow Data in Database
                                    </Button>
                                </Box>
                            </Paper>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 3, justifyContent: 'center' }}>
                    <Button
                        variant="contained"
                        color="success"
                        size="large"
                        onClick={() => {
                            setShowSuccessModal(false);
                            onComplete(workflowResults || {});
                        }}
                        startIcon={<CompleteIcon />}
                        sx={{ minWidth: 250 }}
                    >
                        Close & Complete Workflow
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default WorkflowExecutor;
